/* ═══════════════════════════════════════════════════════════════
   FabScore — Cycle Health Tracker
   healthfab
   ═══════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  // ─── DEV FLAG ────────────────────────────────────────────────
  // Set to true to bypass all API calls (responses are mocked).
  // Set to false to talk to the real backend (the api_base section setting).
  var DEV_SKIP_API = false;

  // True on the local theme-dev host. Used to (a) send the dev-auth bridge
  // headers and (b) expose debug hooks. Never true in production.
  var IS_LOCAL = location.hostname === '127.0.0.1' || location.hostname === 'localhost';

  // We call the backend DIRECTLY at api_base (e.g. https://webserver.healthfab.in).
  // In production the storefront (www.healthfab.in) and backend
  // (webserver.healthfab.in) are subdomains of the same site, so this is a
  // same-site cross-origin call: CORS allows healthfab.in, and the first-party
  // .healthfab.in session cookie (plus the OTP bearer token) flow on
  // credentials:'include'. No Shopify App Proxy is required — FabScore uses its
  // own WhatsApp-OTP session, not Shopify's logged_in_customer_id.
  function apiUrl(path) {
    return CONFIG.apiBase + path;
  }

  // ─── CONFIG ─────────────────────────────────────────────────
  const CONFIG = {
    apiBase: (window.__fabscore && window.__fabscore.apiBase) || 'https://webserver.healthfab.in',
    shopDomain: (window.__fabscore && window.__fabscore.shopDomain) || 'healthfabindia.myshopify.com',
    maxWidth: 430,
  };

  // Local calendar date as 'YYYY-MM-DD' — NOT toISOString(), which is UTC and
  // runs up to ~5.5h behind local date for IST users (e.g. 1am IST is still
  // "yesterday" in UTC), causing false "already checked in today" matches.
  function localDateStr(d) {
    d = d || new Date();
    var y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0'), day = String(d.getDate()).padStart(2, '0');
    return y + '-' + m + '-' + day;
  }
  const TODAY = localDateStr();

  // ─── URL PARAMS ─────────────────────────────────────────────
  function getURLParams() {
    const p = new URLSearchParams(window.location.search);
    return {
      product: p.get('product') || p.get('sku') || null,
      name: p.get('name') || p.get('customer') || null,
      ref: p.get('ref') || p.get('order') || null,
      phase: p.get('phase') || null,
    };
  }

  // ─── ANALYTICS (batched event stream → /api/fabscore/events) ──
  var ANON_KEY = 'fab_anon';
  function getAnonId() {
    try {
      var v = localStorage.getItem(ANON_KEY);
      if (!v) { v = 'a_' + Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem(ANON_KEY, v); }
      return v;
    } catch (e) { return 'a_' + Date.now(); }
  }
  var ANON_ID = getAnonId();
  var SESSION_ID = 's_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
  var _evQueue = [];

  // WhatsApp landing token (?utm_content=...). Captured into sessionStorage on
  // first load — the URL won't carry it through the OTP/login redirect, so we
  // stash it once and read it back from storage on every events call.
  var LANDING_TOKEN_KEY = 'fs_landing_token';
  (function captureLandingToken() {
    try {
      var fromUrl = new URLSearchParams(window.location.search).get('utm_content');
      if (fromUrl) sessionStorage.setItem(LANDING_TOKEN_KEY, fromUrl);
    } catch (e) { }
  })();
  function getLandingToken() {
    try { return sessionStorage.getItem(LANDING_TOKEN_KEY) || null; } catch (e) { return null; }
  }

  function _curPhone() {
    var c = (window.__fabscore && window.__fabscore.customer) || {};
    return (typeof state !== 'undefined' && state.customer && state.customer.phone) || c.phone || null;
  }
  // ─── SHOPIFY WEB PIXELS BRIDGE ──────────────────────────────
  // Mirrors whitelisted events onto Shopify's pixel bus so app pixels and custom
  // pixels can subscribe to them. The first-party pipeline above is unaffected —
  // this is an additional, opt-in copy.
  //
  // Shopify does not allow publishing standard events, so everything is
  // namespaced `fabscore:<event>`. Subscribers read the payload from
  // `customData`, and can catch the lot with `all_custom_events`.
  //
  // Which events go out is a theme-editor setting, so the health-data events
  // (symptom answers, scores, period dates) stay off unless a merchant
  // deliberately adds them.
  var _pixelCfg = null;
  function pixelConfig() {
    if (_pixelCfg) return _pixelCfg;
    var c = window.__fabscore || {};
    // One line per event. A bare name publishes every prop the event carries;
    // "Event: a, b" narrows it to those props, so an event can go out without
    // the parts of its payload that shouldn't leave first-party storage.
    var allow = {};
    String(c.pixelEvents || '').split('\n').forEach(function (line) {
      line = line.trim();
      if (!line) return;
      var sep = line.indexOf(':');
      if (sep === -1) { allow[line] = true; return; }
      var fields = {};
      line.slice(sep + 1).split(',').forEach(function (f) {
        f = f.trim();
        if (f) fields[f] = true;
      });
      allow[line.slice(0, sep).trim()] = fields;
    });
    // "field" → publish the answer; for a multi-select that is the number of
    // options picked. "field: values" → publish which ones, as one event per
    // pick. Joining them into one string instead would mint a fresh dimension
    // value for every combination (2^6 for supplements, 2^8 for concerns),
    // which GA4 buckets into "(other)" and cannot be counted per option.
    var onbAnswers = {};
    String(c.pixelOnboardingAnswers || '').split(/[\n,]/).forEach(function (f) {
      f = f.trim();
      if (!f) return;
      var sep = f.indexOf(':');
      if (sep === -1) { onbAnswers[f] = true; return; }
      onbAnswers[f.slice(0, sep).trim()] = f.slice(sep + 1).trim().toLowerCase() === 'values' ? 'values' : true;
    });
    _pixelCfg = { enabled: !!c.pixelEnabled, allow: allow, onbAnswers: onbAnswers };
    return _pixelCfg;
  }

  // Session_Started → session_started, Login_OtpRequested → login_otp_requested.
  function toEventName(name) {
    return String(name).replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();
  }

  // An option label as a reportable value: "Vitamin D" → vitamin_d,
  // "Skin/acne" → skin_acne. Slugged so the same pick always reads the same in
  // a report, whatever punctuation the label carries.
  function toAnswerValue(label) {
    return String(label).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  }

  function publishToPixels(name, props) {
    var cfg = pixelConfig();
    var fields = cfg.allow[name];
    if (!cfg.enabled || !fields) return;
    try {
      var analytics = window.Shopify && window.Shopify.analytics;
      if (!analytics || typeof analytics.publish !== 'function') return;
      var eventName = 'fabscore:' + toEventName(name);
      var out = {};
      var p = props || {};
      for (var k in p) {
        if (p.hasOwnProperty(k) && (fields === true || fields[k])) out[k] = p[k];
      }
      var payload = Object.assign({
        screen: (typeof state !== 'undefined' && state.screen) || null,
        anonId: ANON_ID,
        sessionId: SESSION_ID,
      }, out);
      analytics.publish(eventName, payload);
    } catch (e) { /* analytics must never break a screen */ }
  }

  // Emit a taxonomy event (Object_Action). Funnel-critical events can force flush.
  function evTrack(name, props, flushNow) {
    _evQueue.push({
      name: name, props: props || {},
      screen: (typeof state !== 'undefined' && state.screen) || null,
      ts: new Date().toISOString(), anonId: ANON_ID, sessionId: SESSION_ID, phone: _curPhone(),
    });
    publishToPixels(name, props);
    if (flushNow || _evQueue.length >= 12) flushEvents();
  }
  function flushEvents(useBeacon) {
    if (DEV_SKIP_API || !_evQueue.length) return;
    var batch = _evQueue.splice(0, _evQueue.length);
    var body = {
      anonId: ANON_ID, sessionId: SESSION_ID, phone: _curPhone(), shopDomain: CONFIG.shopDomain,
      entryProduct: (state.urlParams && state.urlParams.product) || null,
      entryRef: (state.urlParams && state.urlParams.ref) || null,
      events: batch,
      landingToken: getLandingToken(),
    };
    var payload = JSON.stringify(body);
    var url = apiUrl('/api/fabscore/events');
    try {
      // Beacon path (pagehide/visibility): can't set headers. Carries the
      // first-party cookie when same-site (prod); else relies on body.phone.
      if (useBeacon && navigator.sendBeacon) { navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' })); return; }
      var hdr = { 'Content-Type': 'application/json' };
      if (CONFIG.apiBase.indexOf('ngrok') !== -1) hdr['ngrok-skip-browser-warning'] = 'true';
      // Attach the session bearer so the backend trusts the identity over the
      // client-supplied body.phone (attribution hardening). Pre-login there's no
      // token, so ingest stays unauthenticated and falls back to body.phone.
      // credentials:'include' lets the first-party session cookie flow in prod.
      var _tok = getStoredToken();
      if (_tok) hdr['Authorization'] = 'Bearer ' + _tok;
      fetch(url, { method: 'POST', headers: hdr, body: payload, keepalive: true, credentials: 'include' }).catch(function () { });
    } catch (e) { }
  }
  setInterval(function () { flushEvents(); }, 5000);
  window.addEventListener('pagehide', function () { flushEvents(true); });
  document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'hidden') flushEvents(true); });

  // Console mirror (kept for dev visibility).
  function fabscoreTrack(event, data) {
    if (typeof console !== 'undefined') { console.log('[fabscore]', event, data || ''); }
  }

  // ─── SESSION TOKEN (dev/cross-site fallback) ─────────────────
  // In production the session rides in a first-party httpOnly cookie
  // (.healthfab.in) — JS never sees it. On local theme-dev the page (127.0.0.1)
  // and backend (ngrok) are cross-site, so the cookie can't flow; there we keep
  // the token returned by verify-otp in sessionStorage and send it as a Bearer.
  var TOKEN_KEY = 'fab_session_token';
  function getStoredToken() {
    try { return window.sessionStorage.getItem(TOKEN_KEY) || null; } catch (e) { return null; }
  }
  function setStoredToken(t) {
    try { if (t) window.sessionStorage.setItem(TOKEN_KEY, t); else window.sessionStorage.removeItem(TOKEN_KEY); } catch (e) { }
  }

  // ─── API LAYER ──────────────────────────────────────────────
  const fabscoreAPI = {
    _token: getStoredToken(),

    _headers() {
      const h = {
        'Content-Type': 'application/json',
        'x-shopify-shop-domain': CONFIG.shopDomain,
      };
      if (this._token) h['Authorization'] = 'Bearer ' + this._token;
      // Skip ngrok's free-tier browser interstitial so we get JSON, not HTML.
      if (CONFIG.apiBase.indexOf('ngrok') !== -1) h['ngrok-skip-browser-warning'] = 'true';
      // Local test loop only: pass the Liquid-injected customer identity to the
      // backend's dev-auth bridge (FABSCORE_DEV_AUTH=true). In production this is
      // replaced by the App Proxy signed customer id and is never sent.
      var cust = (window.__fabscore && window.__fabscore.customer) || {};
      if (IS_LOCAL && cust.id) {
        h['x-fabscore-customer-id'] = String(cust.id);
        if (cust.email) h['x-fabscore-customer-email'] = cust.email;
      }
      return h;
    },

    // Parses the backend's canonical envelopes:
    //   success: { ok: true, data }
    //   error:   { ok: false, error: { code, message, details } }
    // and the auth middleware's bare { message } 401.
    async _fetch(method, path, body) {
      const opts = { method, headers: this._headers(), credentials: 'include' };
      if (body) opts.body = JSON.stringify(body);
      try {
        const res = await fetch(apiUrl(path), opts);
        let json = {};
        try { json = await res.json(); } catch (e) { json = {}; }
        if (!res.ok) {
          var e = json.error || {};
          return {
            ok: false,
            status: res.status,
            code: e.code || (res.status === 401 ? 'AUTH_REQUIRED' : 'SERVER_ERROR'),
            message: e.message || json.message || 'Something went wrong',
            details: e.details || null,
          };
        }
        return { ok: true, status: res.status, data: json.data, raw: json };
      } catch (err) {
        return { ok: false, status: 0, code: 'NETWORK_ERROR', message: 'You appear to be offline. Check your connection.' };
      }
    },

    getProfile() {
      // Forward BIK attribution params so the backend can tag the visit/session.
      var p = getURLParams();
      var qs = [];
      if (p.product) qs.push('product=' + encodeURIComponent(p.product));
      if (p.ref) qs.push('ref=' + encodeURIComponent(p.ref));
      return this._fetch('GET', '/api/fabscore/profile' + (qs.length ? '?' + qs.join('&') : ''));
    },
    saveProfile(data) { return this._fetch('POST', '/api/fabscore/profile', data); },
    getLog(date) { return this._fetch('GET', '/api/fabscore/log/' + date); },
    getLogs(limit, offset) { return this._fetch('GET', '/api/fabscore/logs?limit=' + (limit || 30) + '&offset=' + (offset || 0)); },
    saveLog(data) { return this._fetch('POST', '/api/fabscore/log', data); },
    setReminderPreference: function (enabled) { return this._fetch('POST', '/api/fabscore/reminders/preference', { enabled: enabled }); },
    getWallet: function () { return this._fetch('GET', '/api/fabscore/wallet'); },
    getPredictions: function () { return this._fetch('GET', '/api/fabscore/predictions'); },
    setPeriodDate: function (date) { return this._fetch('POST', '/api/fabscore/period-date', { lastPeriod: date }); },

    // ── Auth (OTP) ──
    getAuthConfig() { return this._fetch('GET', '/api/fabscore/auth/config'); },
    requestOtp(phone) { return this._fetch('POST', '/api/fabscore/auth/request-otp', { phone: phone, shopDomain: CONFIG.shopDomain }); },
    verifyOtp(phone, otp) {
      var self = this;
      return this._fetch('POST', '/api/fabscore/auth/verify-otp', { phone: phone, otp: otp, shopDomain: CONFIG.shopDomain })
        .then(function (res) {
          // Persist the bearer token for cross-site dev; prod uses the cookie.
          if (res.ok && res.data && res.data.token) { self._token = res.data.token; setStoredToken(res.data.token); }
          return res;
        });
    },
    getSession() { return this._fetch('GET', '/api/fabscore/auth/session'); },
    logout() {
      var self = this;
      return this._fetch('POST', '/api/fabscore/auth/logout', {}).then(function (res) {
        self._token = null; setStoredToken(null); return res;
      });
    },
  };

  // ─── STATE ──────────────────────────────────────────────────
  const state = {
    screen: 'loading',
    customer: (window.__fabscore && window.__fabscore.customer) || { loggedIn: false },
    profile: null,
    todayLog: null,
    history: [],
    earnResult: null,
    walletBalance: null,
    predictions: null,
    predLoading: false,
    onboardStep: 0,
    onboardData: {},
    onboardSaving: false,
    checkinAnswers: {},
    showCheckinHelp: false,
    showInsight: false,
    selectedOpt: null,
    urlParams: getURLParams(),
  };

  function setState(partial) {
    Object.assign(state, partial);
    render();
  }

  // ─── DOM HELPERS ────────────────────────────────────────────
  const $ = (sel, parent) => (parent || document).querySelector(sel);
  const app = () => $('#fabscore-app');

  function h(tag, attrs, ...children) {
    const el = document.createElement(tag);
    if (attrs) {
      for (const [k, v] of Object.entries(attrs)) {
        if (k === 'style' && typeof v === 'object') {
          Object.assign(el.style, v);
        } else if (k.startsWith('on') && typeof v === 'function') {
          el.addEventListener(k.slice(2).toLowerCase(), v);
        } else if (k === 'className') {
          el.className = v;
        } else if (k === 'innerHTML') {
          el.innerHTML = v;
        } else if (v === null || v === false || v === undefined) {
          // Skip empty attributes — e.g. `disabled: null` must NOT render
          // disabled="null" (which would disable the element).
        } else {
          el.setAttribute(k, v === true ? '' : v);
        }
      }
    }
    for (const child of children) {
      if (child == null || child === false) continue;
      if (typeof child === 'string' || typeof child === 'number') {
        el.appendChild(document.createTextNode(child));
      } else if (child instanceof Node) {
        el.appendChild(child);
      }
    }
    return el;
  }

  // ─── ERROR HANDLING ─────────────────────────────────────────
  function handleAPIError(result) {
    if (!result || result.ok) return false;

    if (result.status === 401 || result.code === 'AUTH_REQUIRED' || result.code === 'AUTH_EXPIRED') {
      // Theme Editor has no real session — don't bounce CTA → onboard → login
      if (window.Shopify && window.Shopify.designMode) return true;
      setState({ screen: 'login', loginStep: 'phone' });
      return true;
    }

    return false; // let caller handle other errors
  }

  function renderErrorCard(type, message, onRetry) {
    const titles = {
      network: 'No connection',
      server: 'Something went wrong',
      auth: 'Session expired',
      generic: 'Oops',
    };
    var iconMap = { network: 'alert', server: 'alert', auth: 'shield', generic: 'alert' };

    return h('div', { className: 'hf-error-card' },
      h('div', { className: 'hf-error-card__icon' }, makeIcon(iconMap[type] || 'alert', '#E5484D', 28)),
      h('div', { className: 'hf-error-card__title' }, titles[type] || titles.generic),
      h('div', { className: 'hf-error-card__message' }, message),
      onRetry ? h('button', { className: 'hf-btn', onClick: onRetry }, 'Try again') : null
    );
  }

  // Render an on-brand error card into the app container (no browser alert).
  // `type`: network | server | auth | generic. Picks network automatically for
  // offline results.
  function showError(type, message, onRetry) {
    var container = app();
    if (!container) return;
    container.innerHTML = '';
    container.appendChild(renderErrorCard(type, message, onRetry));
  }

  // Map an API result to the right error-card type.
  function errTypeOf(res) {
    if (!res) return 'generic';
    if (res.code === 'NETWORK_ERROR' || res.status === 0) return 'network';
    if (res.status === 401) return 'auth';
    if (res.status >= 500) return 'server';
    return 'generic';
  }

  // Prefer Joi field messages over the bare "Validation failed" label.
  function apiErrorMessage(res, fallback) {
    var fields = res && res.details && res.details.fields;
    if (fields && fields.length) {
      return fields.map(function (f) { return f.message; }).filter(Boolean).join(' · ') || (res.message || fallback);
    }
    return (res && res.message) || fallback || 'Something went wrong';
  }

  // ─── SCREEN: LOADING ───────────────────────────────────────
  function renderLoading() {
    return h('div', { className: 'hf-loading' },
      h('div', { className: 'hf-loading__icon' }, makeIcon('heart', '#0C2B82', 32)),
      h('p', { className: 'hf-loading__text' }, 'Loading your health…')
    );
  }

  // ─── SCREEN: LOGIN ─────────────────────────────────────────
  // Shopify hosted login link (used only when OTP is disabled from the backend).
  function shopifyLoginUrl() {
    const fb = window.__fabscore || {};
    const returnTo = (fb.currentUrl || '/pages/fabscore') + (window.location.search || '');
    const newAcctLogin = '/customer_authentication/login?return_to=' + encodeURIComponent(returnTo);
    return IS_LOCAL ? (fb.loginUrlFallback || '/account/login') : newAcctLogin;
  }

  // ── Inline icons (white WhatsApp glyph, small WA, lock, chat bubble badge) ──
  var SVG_WA = '<svg width="20" height="20" viewBox="0 0 32 32" fill="#fff" xmlns="http://www.w3.org/2000/svg"><path d="M16 .5C7.4.5.5 7.4.5 16c0 2.8.7 5.5 2.1 7.9L.4 31.6l7.9-2.1c2.3 1.3 4.9 1.9 7.7 1.9C24.6 31.4 31.5 24.5 31.5 16S24.6.5 16 .5zm0 28c-2.5 0-4.9-.7-7-1.9l-.5-.3-4.7 1.2 1.3-4.6-.3-.5C3.5 21.2 2.8 18.7 2.8 16 2.8 8.7 8.7 2.8 16 2.8S29.2 8.7 29.2 16 23.3 28.5 16 28.5zm7.2-9.4c-.4-.2-2.3-1.1-2.7-1.3-.4-.1-.6-.2-.9.2-.3.4-1 1.3-1.2 1.5-.2.2-.4.3-.8.1-.4-.2-1.6-.6-3.1-1.9-1.1-1-1.9-2.3-2.1-2.7-.2-.4 0-.6.2-.8.2-.2.4-.4.5-.7.2-.2.2-.4.4-.6.1-.3 0-.5 0-.7-.1-.2-.9-2.1-1.2-2.9-.3-.7-.6-.6-.9-.7h-.7c-.2 0-.6.1-.9.4-.3.4-1.2 1.2-1.2 2.9s1.2 3.4 1.4 3.6c.2.2 2.5 3.8 6 5.3.8.4 1.5.6 2 .7.8.3 1.6.2 2.2.1.7-.1 2.3-.9 2.6-1.8.3-.9.3-1.6.2-1.8-.1-.1-.3-.2-.7-.4z"/></svg>';
  var SVG_WA_SM = '<svg width="14" height="14" viewBox="0 0 32 32" fill="#25D366" xmlns="http://www.w3.org/2000/svg"><path d="M16 .5C7.4.5.5 7.4.5 16c0 2.8.7 5.5 2.1 7.9L.4 31.6l7.9-2.1c2.3 1.3 4.9 1.9 7.7 1.9C24.6 31.4 31.5 24.5 31.5 16S24.6.5 16 .5zm7.2 19.1c-.4-.2-2.3-1.1-2.7-1.3-.4-.1-.6-.2-.9.2-.3.4-1 1.3-1.2 1.5-.2.2-.4.3-.8.1-.4-.2-1.6-.6-3.1-1.9-1.1-1-1.9-2.3-2.1-2.7-.2-.4 0-.6.2-.8.2-.2.4-.4.5-.7.2-.2.2-.4.4-.6.1-.3 0-.5 0-.7-.1-.2-.9-2.1-1.2-2.9-.3-.7-.6-.6-.9-.7h-.7c-.2 0-.6.1-.9.4-.3.4-1.2 1.2-1.2 2.9s1.2 3.4 1.4 3.6c.2.2 2.5 3.8 6 5.3.8.4 1.5.6 2 .7.8.3 1.6.2 2.2.1.7-.1 2.3-.9 2.6-1.8.3-.9.3-1.6.2-1.8z"/></svg>';
  var SVG_LOCK = '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#999" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="11" width="16" height="9" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>';
  var SVG_BUBBLE = '<svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.5 8.5 0 0 1-3.8-.9L3 21l1.9-5.7A8.38 8.38 0 0 1 4 11.5 8.5 8.5 0 0 1 12.5 3 8.38 8.38 0 0 1 21 11.5z"/></svg>';
  function ic(svg) { return h('span', { innerHTML: svg, style: { display: 'inline-flex' } }); }

  function renderLogin() {
    const fb = window.__fabscore || {};
    const logoUrl = fb.logoUrl || '';
    const cfg = state.authConfig || { methods: { otp: true, shopify: true } };

    // Backend toggle: OTP disabled, Shopify enabled → show the Shopify link.
    if (cfg.methods && cfg.methods.otp === false && cfg.methods.shopify) {
      return h('div', { className: 'hf-auth' },
        h('div', { className: 'hf-auth__card' },
          logoUrl ? h('img', { className: 'hf-auth__logo', src: logoUrl, alt: 'HealthFab' }) : h('div', { className: 'hf-auth__badge' }, ic(SVG_BUBBLE)),
          h('h1', { className: 'hf-auth__title' }, 'Log in to continue'),
          h('p', { className: 'hf-auth__sub' }, 'Your cycle health data is private and tied to your HealthFab account.'),
          h('a', { href: shopifyLoginUrl(), className: 'hf-auth__cta', style: { textDecoration: 'none' } }, 'Log in to HealthFab')
        )
      );
    }

    const step = state.loginStep || 'phone';
    const card = h('div', { className: 'hf-auth__card' });
    card.appendChild(logoUrl
      ? h('img', { className: 'hf-auth__logo', src: logoUrl, alt: 'HealthFab' })
      : h('div', { className: 'hf-auth__badge' }, ic(SVG_BUBBLE)));

    if (step === 'otp') {
      card.appendChild(h('h1', { className: 'hf-auth__title' }, 'Enter your code'));
      card.appendChild(h('div', { className: 'hf-auth__chip' }, ic(SVG_WA_SM), 'sent to ' + (state.loginMasked || 'your WhatsApp')));

      var boxes = h('div', { className: 'hf-otp' });
      var dev = state.loginDevOtp || '';
      for (var i = 0; i < 6; i++) {
        (function (idx) {
          boxes.appendChild(h('input', {
            id: 'hf-otp-' + idx, className: 'hf-otp__box' + (dev[idx] ? ' is-filled' : ''),
            type: 'tel', inputmode: 'numeric', maxlength: '1',
            autocomplete: idx === 0 ? 'one-time-code' : 'off', value: dev[idx] || '',
            oninput: function (e) { onOtpInput(e, idx); },
            onkeydown: function (e) { onOtpKey(e, idx); },
            onpaste: function (e) { onOtpPaste(e); }
          }));
        })(i);
      }
      card.appendChild(boxes);

      if (state.loginError) card.appendChild(h('p', { className: 'hf-auth__error' }, state.loginError));
      card.appendChild(h('button', {
        className: 'hf-auth__cta', disabled: state.loginBusy ? 'disabled' : null,
        onClick: function () { verifyCode(); }
      }, state.loginBusy ? 'Verifying…' : 'Verify & continue'));
      card.appendChild(h('div', { className: 'hf-auth__row' },
        h('button', { className: 'hf-auth__link', onClick: function () { stopResendCountdown(); setState({ loginStep: 'phone', loginError: null, loginDevOtp: null }); focusEl('hf-phone-input'); } }, '‹ Change number'),
        h('button', { id: 'hf-resend', className: 'hf-auth__link', onClick: function () { sendCode(state.loginPhone); } }, 'Resend code')
      ));
      if (dev) card.appendChild(h('p', { className: 'hf-auth__dev' }, 'dev code: ' + dev));
    } else {
      card.appendChild(h('h1', { className: 'hf-auth__title' }, 'Sign in to FabScore'));
      card.appendChild(h('p', { className: 'hf-auth__sub', innerHTML: 'Track your cycle health. We\'ll text a one-time code to your <b>WhatsApp</b>.' }));
      card.appendChild(h('div', { className: 'hf-auth__field' },
        h('label', { className: 'hf-auth__flabel' }, 'Mobile number'),
        h('div', { className: 'hf-phone' },
          h('span', { className: 'hf-phone__cc' }, '🇮🇳 +91'),
          h('input', {
            id: 'hf-phone-input', className: 'hf-phone__input', type: 'tel', inputmode: 'numeric',
            maxlength: '10', placeholder: '90000 00000', autocomplete: 'tel-national', value: state.loginPhone || '',
            onkeydown: function (e) { if (e.key === 'Enter') sendCode(valOf('hf-phone-input')); }
          })
        )
      ));
      if (state.loginError) card.appendChild(h('p', { className: 'hf-auth__error' }, state.loginError));
      card.appendChild(h('button', {
        className: 'hf-auth__cta', disabled: state.loginBusy ? 'disabled' : null,
        onClick: function () { sendCode(valOf('hf-phone-input')); }
      }, ic(SVG_WA), state.loginBusy ? 'Sending…' : 'Send code on WhatsApp'));
    }

    return h('div', { className: 'hf-auth' },
      card,
      h('p', { className: 'hf-auth__foot' }, ic(SVG_LOCK), 'Private & encrypted — only you can see your data')
    );
  }

  // ── Login helpers ──
  function valOf(id) { var el = document.getElementById(id); return el ? el.value : ''; }
  function focusEl(id) { setTimeout(function () { var el = document.getElementById(id); if (el) el.focus(); }, 30); }
  function readOtp() { var s = ''; for (var i = 0; i < 6; i++) s += valOf('hf-otp-' + i); return s; }
  function updateOtpFilled() {
    for (var i = 0; i < 6; i++) { var b = document.getElementById('hf-otp-' + i); if (b) b.classList.toggle('is-filled', !!b.value); }
  }
  function onOtpInput(e, i) {
    var v = e.target.value.replace(/\D/g, '');
    e.target.value = v.slice(-1);
    if (v && i < 5) focusEl('hf-otp-' + (i + 1));
    updateOtpFilled();
    if (i === 5 && readOtp().length === 6) verifyCode();
  }
  function onOtpKey(e, i) {
    if (e.key === 'Backspace' && !e.target.value && i > 0) { var p = document.getElementById('hf-otp-' + (i - 1)); if (p) { p.value = ''; p.focus(); } updateOtpFilled(); }
    else if (e.key === 'Enter') verifyCode();
  }
  function onOtpPaste(e) {
    var t = ((e.clipboardData || window.clipboardData).getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (!t) return;
    e.preventDefault();
    for (var i = 0; i < 6; i++) { var b = document.getElementById('hf-otp-' + i); if (b) b.value = t[i] || ''; }
    updateOtpFilled();
    focusEl('hf-otp-' + (Math.min(t.length, 6) - 1));
    if (t.length === 6) verifyCode();
  }

  // Resend countdown — updates the button text via DOM (no re-render, so OTP
  // box values/focus are preserved).
  var _resendTimer = null;
  function stopResendCountdown() { if (_resendTimer) { clearInterval(_resendTimer); _resendTimer = null; } }
  function startResendCountdown(sec) {
    stopResendCountdown();
    var left = sec;
    var paint = function () {
      var b = document.getElementById('hf-resend');
      if (!b) { stopResendCountdown(); return; }
      if (left <= 0) { stopResendCountdown(); b.disabled = false; b.textContent = 'Resend code'; return; }
      b.disabled = true; b.textContent = 'Resend in ' + left + 's';
    };
    paint();
    _resendTimer = setInterval(function () { left--; paint(); }, 1000);
  }

  // ── Login handlers ──
  function sendCode(phone) {
    phone = (phone || '').trim();
    if (!phone) { setState({ loginError: 'Please enter your mobile number.' }); focusEl('hf-phone-input'); return; }
    state.loginPhone = phone;
    evTrack('Login_OtpRequested', {});
    setState({ loginBusy: true, loginError: null });
    fabscoreAPI.requestOtp(phone).then(function (res) {
      if (res.ok) {
        setState({ loginStep: 'otp', loginMasked: res.data && res.data.masked, loginDevOtp: (res.data && res.data.devOtp) || null, loginBusy: false, loginError: null });
        focusEl('hf-otp-0');
        startResendCountdown(30);
      } else {
        setState({ loginBusy: false, loginError: res.message || 'Could not send the code. Please try again.' });
      }
    });
  }

  function verifyCode() {
    var otp = readOtp();
    if (!/^\d{6}$/.test(otp)) { setState({ loginError: 'Enter the 6-digit code.' }); return; }
    setState({ loginBusy: true, loginError: null });
    fabscoreAPI.verifyOtp(state.loginPhone, otp).then(function (res) {
      if (res.ok) {
        stopResendCountdown();
        state.customer = { loggedIn: true, phone: res.data && res.data.phone };
        state.loginBusy = false; state.loginError = null; state.loginDevOtp = null;
        evTrack('Login_OtpVerified', {});
        evTrack('Identify', {}, true); // force flush → backend stitches anonId → phone
        routeAfterAuth();
      } else {
        setState({ loginBusy: false, loginError: res.message || 'Verification failed. Please try again.' });
        focusEl('hf-otp-0');
      }
    });
  }

  // ─── WELCOME DATA ────────────────────────────────────────────
  // ─── PHASES ──────────────────────────────────────────────────
  const PHASES = {
    menstrual: { key: 'menstrual', label: 'Period', iconKey: 'drop', color: '#993556', bg: '#FBEAF0', border: '#F4C0D1', days: 'Days 1–5' },
    follicular: { key: 'follicular', label: 'Follicular', iconKey: 'bolt', color: '#0F6E56', bg: '#E1F5EE', border: '#9FE1CB', days: 'Days 6–13' },
    ovulatory: { key: 'ovulatory', label: 'Ovulation', iconKey: 'star', color: '#854F0B', bg: '#FAEEDA', border: '#FAC775', days: 'Days 14–16' },
    luteal: { key: 'luteal', label: 'Luteal', iconKey: 'moon', color: '#3C3489', bg: '#EEEDFE', border: '#CECBF6', days: 'Days 17–28' },
  };

  // ─── PRODUCT CATALOG (fallback keys for URL routing when Liquid data missing) ──
  const PRODUCT_CATALOG = {
    period_underwear: {},
    iron_b12: {},
    magnesium: {},
    pain_relief: {},
    bloat_oil: {},
    default: {},
  };

  // ─── TOUCHPOINTS ────────────────────────────────────────────
  const TOUCHPOINTS = {
    M1: {
      id: 'M1', day: 1, label: 'Day 1', phase_label: 'Period started',
      color: '#E5484D', bg: '#FFF0F0', border: '#FFCFCF',
      heading: 'your period just started',
      subhead: 'one question. takes 10 seconds.',
      formula: 'Regularity · 25% of your score',
      questions: [
        {
          id: 'M1Q1', ask: 'How bad are your cramps right now?', options: [
            { l: 'No cramps at all', s: 95, vibe: 'bestie you\'re winning' },
            { l: 'Mild — barely there', s: 78, vibe: 'totally manageable' },
            { l: 'Moderate — I notice them', s: 52, vibe: 'classic day 1 energy' },
            { l: 'Bad — hard to function', s: 22, vibe: 'this deserves attention' },
          ]
        },
      ],
      insights: function (ans) {
        var s = (ans && ans.M1Q1 && ans.M1Q1.s) || 70;
        if (s >= 85) return ['No cramps on day 1 = strong progesterone exit. Your cycle is doing something right.', 'Your iron loss is starting but your body is handling it well.', 'Track this over 3 months — consistent no-cramp patterns are gold data.'];
        if (s >= 65) return ['Mild day 1 cramps are totally normal — prostaglandins peak in the first 24 hours.', 'This should ease by day 3. If it doesn\'t, that\'s worth noting.', 'Take your Iron + B12 today. Absorption is lowest right now but still worth it.'];
        if (s >= 40) return ['Moderate cramps = your prostaglandin levels are elevated. Not abnormal, but worth tracking.', 'If this is cycle 2+ at this level, we\'re building a pattern. That\'s valuable data.', 'Magnesium 2 weeks before your next period can reduce this by up to 34%.'];
        return ['Severe cramps on day 1 are a flag, not just bad luck. We\'re tracking this.', 'Pain above 7/10 for 3 consecutive cycles = clinical review recommended.', 'You should know: 1 in 10 women with severe day 1 pain has undiagnosed endometriosis.'];
      }
    },
    M2: {
      id: 'M2', day: 2, label: 'Day 2', phase_label: 'Peak symptoms',
      color: '#E79D13', bg: '#FFF8E1', border: '#FFE4A0',
      heading: 'day 2 check-in',
      subhead: 'this is usually the hardest day. how are you doing?',
      formula: 'Symptom trend · 20% of your score',
      questions: [
        {
          id: 'M2Q1', ask: 'Energy levels this morning?', options: [
            { l: 'Good — I actually got up fine', s: 92, vibe: 'wait, a good day 2??' },
            { l: 'Meh — needed effort but okay', s: 65, vibe: 'relatable honestly' },
            { l: 'Low — running on empty', s: 38, vibe: 'iron depletion doing its thing' },
            { l: 'Exhausted — can barely function', s: 15, vibe: 'your body is asking for rest' },
          ]
        },
      ],
      insights: function (ans) {
        var s = (ans && ans.M2Q1 && ans.M2Q1.s) || 70;
        if (s >= 85) return ['High energy on day 2 is actually a hormonal green flag.', 'Your cortisol awakening response is strong — this is measurable and a good sign.', 'Day 2 energy often predicts how the rest of your cycle will feel. This is promising.'];
        if (s >= 55) return ['Average day 2 energy. Estrogen is at its lowest right now — this is why.', 'By day 6 you\'ll feel noticeably different. The hormonal rebound is real.', 'Iron-rich food today will help more than coffee. Dal, eggs, leafy greens.'];
        if (s >= 30) return ['Low energy on day 2 = iron depletion from blood loss is happening fast.', 'This is the most common sign of subclinical iron deficiency — and the most ignored.', 'If this happens every cycle, a serum ferritin test is worth it. It answers everything.'];
        return ['Severe fatigue on day 2 is your body sending a loud signal.', 'This level of exhaustion isn\'t just period normal — it\'s worth investigating.', 'Get a CBC blood test this week. Share the results and we\'ll tell you what they mean.'];
      }
    },
    M3: {
      id: 'M3', day: 14, label: 'Day 14', phase_label: 'Peak week',
      color: '#30A46C', bg: '#E8FAF0', border: '#9FE1CB',
      heading: 'you\'re at your monthly peak',
      subhead: 'one question about what you\'ve been eating this week',
      formula: 'Nutrition · 15% of your score',
      questions: [
        {
          id: 'M3Q1', ask: 'Iron-rich food this week?', options: [
            { l: 'Yes — dal, eggs, meat, greens daily', s: 95, vibe: 'your body is being fed right' },
            { l: 'Some — a few times', s: 68, vibe: 'not bad, can do better' },
            { l: 'Barely — mostly carbs this week', s: 38, vibe: 'your iron will feel this' },
            { l: 'No — haven\'t thought about it', s: 20, vibe: 'let\'s fix this starting today' },
          ]
        },
      ],
      insights: function (ans) {
        var s = (ans && ans.M3Q1 && ans.M3Q1.s) || 70;
        if (s >= 85) return ['Consistent iron intake in follicular phase = faster recovery after your next period.', 'This is the window when iron absorption is highest. You\'re using it well.', 'Keep this up and your day 2 energy score should improve over 2-3 cycles.'];
        if (s >= 55) return ['Some iron intake is better than none — but this week it matters most.', 'Estrogen helps iron absorb better in follicular phase. You\'re leaving gains on the table.', 'Add one iron-rich meal every day for the next 7 days. It will show up in your next period-start score.'];
        return ['Low iron intake in your peak absorption week means next cycle\'s day 2 will be harder.', 'This is the most impactful single thing you can change. One extra dal or egg a day.', 'Your score will directly reflect this in 28 days. The data doesn\'t lie.'];
      }
    },
    M4: {
      id: 'M4', day: 22, label: 'Day 22', phase_label: 'Pre-period week',
      color: '#6E56CF', bg: '#F0EFFE', border: '#CECBF6',
      heading: 'pre-period check',
      subhead: 'one question. this week is all about progesterone.',
      formula: 'Symptom + Sleep · 30% of your score',
      questions: [
        {
          id: 'M4Q1', ask: 'Bloating today?', options: [
            { l: 'None — feeling normal', s: 92, vibe: 'magnesium is working' },
            { l: 'Mild — a bit puffy', s: 68, vibe: 'typical luteal, manageable' },
            { l: 'Moderate — noticeably bloated', s: 42, vibe: 'progesterone is loud today' },
            { l: 'Severe — really uncomfortable', s: 18, vibe: 'this needs attention' },
          ]
        },
      ],
      insights: function (ans) {
        var s = (ans && ans.M4Q1 && ans.M4Q1.s) || 70;
        if (s >= 85) return ['No bloating on day 22 = your gut motility is handling progesterone well.', 'This is genuinely good news — it often means your magnesium levels are adequate.', 'Track this next cycle too. Consistent no-bloat luteal phases are a real health signal.'];
        if (s >= 55) return ['Mild luteal bloating is normal — progesterone slows gut motility by 30%.', 'This is hormonal, not food. Cutting salt helps, but magnesium helps more.', 'Days 17-24 magnesium supplementation reduces this in most women within 2-3 cycles.'];
        if (s >= 30) return ['Moderate bloating this late in your cycle = progesterone sensitivity is high.', 'This often pairs with sleep disruption and mood changes — watch for those too.', 'Magnesium glycinate at 400mg before bed, starting from day 17 next cycle.'];
        return ['Severe luteal bloating is a signal worth taking seriously.', 'Combined with mood changes and sleep issues, this pattern has a name: PMDD.', 'Log this for one more cycle then talk to a gynaec. You don\'t have to just endure this.'];
      }
    },
    M5: {
      id: 'M5', dayMin: 23, dayMax: 28, label: 'Days 23-28', phase_label: 'Sleep tracking',
      color: '#6E56CF', bg: '#F0EFFE', border: '#CECBF6',
      heading: 'sleep check',
      subhead: 'literally one question. 10 seconds.',
      formula: 'Sleep · 10% of your score',
      questions: [
        {
          id: 'M5Q1', ask: 'Sleep quality last night?', options: [
            { l: 'Slept through — felt rested', s: 95, vibe: 'rare and precious in luteal' },
            { l: 'Okay — woke once', s: 68, vibe: 'progesterone doing normal things' },
            { l: 'Poor — woke several times', s: 38, vibe: 'classic luteal sleep' },
            { l: 'Very broken — barely slept', s: 15, vibe: 'your body needs support' },
          ]
        },
      ],
      insights: function (ans) {
        var s = (ans && ans.M5Q1 && ans.M5Q1.s) || 70;
        if (s >= 85) return ['Unbroken sleep in luteal phase = rare. You\'re either magnesium-adequate or very lucky.', 'Your body temperature is elevated this week — your cooling system is working well.', 'This sleep data is the single best predictor of how your day 1 cramps will feel.'];
        if (s >= 55) return ['Waking once in luteal is normal — progesterone raises body temp and lightens sleep.', 'This should resolve within 2-3 days of your period starting.', 'Magnesium before bed and keeping your room cooler than usual helps more than you\'d think.'];
        return ['Broken luteal sleep is one of the most undertreated cycle symptoms.', 'It\'s directly caused by progesterone — which means it\'s predictable and manageable.', 'Magnesium glycinate (not oxide) 400mg before bed starting day 17. The difference is real.'];
      }
    },
    N1: {
      id: 'N1', label: 'Period check', phase_label: 'Period approaching',
      color: '#E5484D', bg: '#FFF0F0', border: '#FFCFCF',
      heading: 'is your period here yet?',
      subhead: 'we check daily until you confirm — irregular is normal',
      formula: 'Regularity · 25% of your score',
      questions: [
        {
          id: 'N1Q1', ask: 'Has your period started today?', options: [
            { l: 'Yes — started today', s: 95, vibe: 'locked in, updating your cycle' },
            { l: 'Spotting or light bleeding', s: 72, vibe: 'counting this, tracking it' },
            { l: 'Not yet — no signs', s: 85, vibe: 'we\'ll check again tomorrow' },
            { l: 'Seems late or irregular', s: 65, vibe: 'noted — PCOS aware mode on' },
          ]
        },
      ],
      insights: function () {
        return ['Your predicted cycle date has been updated.', 'Your next check-ins are now scheduled to match your actual cycle.', 'Irregular periods are data, not a problem — we track them the same way.'];
      }
    },
  };

  // ─── ACTION PLANS PER GRADE ─────────────────────────────────
  const ACTION_PLANS = {
    A: {
      title: 'Keep doing what you\'re doing',
      color: '#2BA84A', bg: '#F0FBF2',
      actions: [
        { iconKey: 'pill', label: 'Supplements', text: 'Continue current routine — your timing and consistency are working' },
        { iconKey: 'calendar', label: 'Next check-in', text: 'Log your mid-cycle nutrition check on cycle day 14 to maintain this score' },
        { iconKey: 'shield', label: 'Health test', text: 'Annual blood panel is enough — CBC, iron, vitamin D once a year' },
      ]
    },
    B: {
      title: 'Small gaps — here\'s your action plan',
      color: '#0891B2', bg: '#F0FAFB',
      actions: [
        { iconKey: 'drop', label: 'Blood test this month', text: 'Get a CBC + serum ferritin test. Low ferritin is the most common hidden cause of fatigue and mood issues.' },
        { iconKey: 'pill', label: 'Supplement timing', text: 'Take Iron + B12 before 10 AM with vitamin C. Avoid tea or coffee for 1 hour after.' },
        { iconKey: 'moon', label: 'Add magnesium', text: 'Start magnesium from cycle day 17. This is when progesterone peaks and magnesium is most effective.' },
        { iconKey: 'chart', label: 'Track for 2 more cycles', text: 'Your score improves to A range when 2 consecutive cycles show consistent patterns.' },
      ]
    },
    C: {
      title: 'Addressable — take these steps now',
      color: '#D97706', bg: '#FFFBEB',
      actions: [
        { iconKey: 'drop', label: 'Blood test — urgent', text: 'Get CBC + serum ferritin + vitamin D + thyroid (TSH) panel. Multiple C-range flags often have a single root cause.' },
        { iconKey: 'pill', label: 'Iron + B12 daily', text: 'Take every day without skipping. Results take 6–8 weeks to show in blood.' },
        { iconKey: 'moon', label: 'Magnesium at night', text: 'Start from day 17. At 400mg before bed, it reduces luteal bloating and improves sleep within 2–3 cycles.' },
        { iconKey: 'heart', label: 'Food this week', text: 'Dal, spinach, eggs and meat 4+ times this week. Vitamin C alongside every iron-rich meal.' },
        { iconKey: 'shield', label: 'Consider gynaec review', text: 'If your score stays in C range after 2 cycles, a gynaecologist can rule out PCOS, thyroid, or iron deficiency anaemia.' },
      ]
    },
    D: {
      title: 'See a doctor — your score has clinical flags',
      color: '#E53E3E', bg: '#FFF5F5',
      actions: [
        { iconKey: 'alert', label: 'Book a gynaec this week', text: 'Your score has multiple clinical flags. A doctor needs to see your full symptom picture.' },
        { iconKey: 'drop', label: 'Get these tests done', text: 'CBC · Serum ferritin · TSH · Vitamin D · Fasting insulin (if irregular cycles). Take results to your doctor.' },
        { iconKey: 'pill', label: 'Don\'t self-supplement yet', text: 'Until blood tests confirm what\'s low, avoid mega-dosing. Take existing supplements as directed.' },
        { iconKey: 'chart', label: 'Bring your report', text: 'Your score breakdown shows pattern data across multiple touchpoints — share it with your doctor.' },
      ]
    }
  };

  // ─── HELPER FUNCTIONS ───────────────────────────────────────
  function getDayOfCycle(lastPeriod, cycleLength) {
    if (!lastPeriod) return 1;
    var lp = new Date(lastPeriod + 'T00:00:00');
    var now = new Date();
    var diff = Math.floor((now - lp) / 86400000);
    var cl = cycleLength || 28;
    // diff=0 (period started today) must map to day 1, not day `cl` — days
    // are 1-indexed (day 1..cl), so normalize into [0, cl) then shift by 1.
    return (((diff % cl) + cl) % cl) + 1;
  }

  function getPhase(day) {
    if (day <= 5) return PHASES.menstrual;
    if (day <= 13) return PHASES.follicular;
    if (day <= 16) return PHASES.ovulatory;
    return PHASES.luteal;
  }

  function getGrade(score) {
    if (score >= 80) return { grade: 'A', label: 'Excellent', color: '#D4537E', ringColor: '#D4537E', bg: '#FFF0F6', desc: 'Your cycle health is strong. Keep doing what you\'re doing.' };
    if (score >= 60) return { grade: 'B', label: 'Good', color: '#D4537E', ringColor: '#D4537E', bg: '#FFF0F6', desc: 'Small gaps to address — you\'re close to excellent.' };
    if (score >= 40) return { grade: 'C', label: 'Needs Attention', color: '#D4537E', ringColor: '#D97706', bg: '#FFFBEB', desc: 'Addressable issues found. Follow the action plan below.' };
    return { grade: 'D', label: 'Clinical Review', color: '#E53E3E', ringColor: '#E53E3E', bg: '#FFF5F5', desc: 'Your score has clinical flags. Please see a doctor.' };
  }

  function getTouchpoint(cycleDay, history, profile) {
    if (history && history.some(function (h) { return h.date === TODAY; })) return null;
    if (!profile || !profile.lastPeriod || !profile.cycleLength) return null;

    var lp = new Date(profile.lastPeriod + 'T00:00:00');
    var nextPeriod = new Date(lp.getTime() + profile.cycleLength * 86400000);
    var daysUntilPeriod = Math.round((nextPeriod - new Date()) / 86400000);

    var cycleStartDate = new Date();
    cycleStartDate.setDate(cycleStartDate.getDate() - cycleDay);
    var cycleStart = localDateStr(cycleStartDate);
    var logged = function (id) { return history && history.some(function (h) { return h.touchpointId === id && h.date >= cycleStart; }); };

    if (!logged('M1') && daysUntilPeriod <= 5 && daysUntilPeriod >= -15) return TOUCHPOINTS.N1;
    if (cycleDay === 1 && !logged('M1')) return TOUCHPOINTS.M1;
    if (cycleDay === 2 && logged('M1') && !logged('M2')) return TOUCHPOINTS.M2;
    if (cycleDay === 14 && !logged('M3')) return TOUCHPOINTS.M3;
    if (cycleDay === 22 && !logged('M4')) return TOUCHPOINTS.M4;
    if (cycleDay >= 23 && cycleDay <= 28 && !logged('M5')) return TOUCHPOINTS.M5;
    return null;
  }

  function getNextTouchpoint(cycleDay) {
    var pts = [1, 2, 14, 22];
    var next = null;
    for (var i = 0; i < pts.length; i++) {
      if (pts[i] > cycleDay) { next = pts[i]; break; }
    }
    if (next) return { day: next, daysAway: next - cycleDay };
    return { day: 1, daysAway: 28 - cycleDay + 1 };
  }

  // Formats a 'YYYY-MM-DD' date string as a short friendly date, e.g. "15 Jun".
  function fmtDate(s) {
    return s ? new Date(s + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '';
  }

  // Formats a relative day offset as a friendly date, e.g. "Mon, 15 Jun".
  function formatNextDate(daysAway) {
    var d = new Date(Date.now() + daysAway * 86400000);
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  }

  // Human label for the next touchpoint, keyed by cycle day (extends TP_NAMES).
  // Days not in the map (e.g. 23-28 / M5) fall back to "Sleep check".
  function nextTouchpointLabel(day) {
    return (typeof TP_NAMES !== 'undefined' && TP_NAMES[day]) || 'Sleep check';
  }

  function getProductFromURL(catalog) {
    var params = new URLSearchParams(window.location.search);
    var sku = params.get('sku') || params.get('product') || 'default';
    if (sku.indexOf('iron') !== -1 || sku.indexOf('b12') !== -1) return 'iron_b12';
    if (sku.indexOf('magnesium') !== -1 || sku.indexOf('mag') !== -1) return 'magnesium';
    if (sku.indexOf('pain') !== -1 || sku.indexOf('roll') !== -1) return 'pain_relief';
    if (sku.indexOf('bloat') !== -1 || sku.indexOf('oil') !== -1) return 'bloat_oil';
    if (sku.indexOf('pad') !== -1 || sku.indexOf('underwear') !== -1 || sku.indexOf('gpf') !== -1) return 'period_underwear';
    var keys = catalog || PRODUCT_CATALOG;
    return keys[sku] ? sku : 'default';
  }

  function getProductFirstData() {
    var el = document.getElementById('hf-product-first-data');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function hydrateProductFirst() {
    var data = getProductFirstData();
    if (!data || !data.catalog) return;
    var productKey = getProductFromURL(data.catalog);
    var p = data.catalog[productKey] || data.catalog.default;
    var facts = (data.problems && (data.problems[productKey] || data.problems.default)) || [];

    var hookEl = document.getElementById('hf-product-first-hook');
    if (hookEl) hookEl.textContent = p.hook || '';

    var cardsEl = document.getElementById('hf-product-first-cards');
    if (!cardsEl) return;
    if (cardsEl.dataset.hydrated === productKey) return;
    cardsEl.dataset.hydrated = productKey;
    cardsEl.innerHTML = '';

    facts.forEach(function (fact) {
      var iconContainer = h('div', {
        style: {
          width: '40px', height: '40px', borderRadius: '12px',
          background: fact.accent + '12', display: 'flex',
          alignItems: 'center', justifyContent: 'center', flexShrink: '0'
        }
      });
      iconContainer.appendChild(makeIcon(fact.icon, fact.accent, 20));

      var card = h('div', {
        className: 'hf-fact-card',
        style: { borderLeft: '3px solid ' + fact.accent }
      }, iconContainer, h('div', null,
        h('p', { style: { fontSize: '22px', fontWeight: '800', color: '#D4537E', margin: '0 0 4px', letterSpacing: '-0.3px' } }, fact.stat),
        h('p', { style: { fontSize: '13px', color: '#555', margin: '0', lineHeight: '1.5' } }, fact.text)
      ));
      cardsEl.appendChild(card);
    });

    cardsEl.appendChild(h('div', { className: 'hf-product-first__promise-card' },
      h('p', { className: 'hf-product-first__promise-label' }, 'What we\'ll do for you'),
      h('p', { className: 'hf-product-first__promise-short' }, p.shortTip || ''),
      h('p', { className: 'hf-product-first__promise-long' }, p.longTip || '')
    ));
  }

  function calcScore(answers, phase, history) {
    var vals = [];
    for (var k in answers) {
      if (answers.hasOwnProperty(k) && answers[k] && typeof answers[k].s === 'number') {
        vals.push(answers[k].s);
      }
    }
    if (vals.length === 0) return 50;
    var sum = 0;
    for (var i = 0; i < vals.length; i++) sum += vals[i];
    return Math.round(sum / vals.length);
  }

  // ─── Carousel scroll helper ───────────────────────────────
  function scrollInsightTo(i) {
    var track = document.getElementById('hf-insight-track');
    if (!track || !track.children[i]) return;

    // Lock scroll observer so it doesn't fight the programmatic scroll
    track._scrollLock = true;
    clearTimeout(track._scrollLockTimer);
    track._scrollLockTimer = setTimeout(function () { track._scrollLock = false; }, 400);

    // Disable snap → instant jump to card → re-enable snap on next frame
    track.style.scrollSnapType = 'none';
    track.scrollLeft = track.children[i].offsetLeft - 16;
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        track.style.scrollSnapType = '';
      });
    });

    // Update state + UI directly (no re-render)
    state.welcomeInsight = i;
    document.querySelectorAll('.hf-welcome__phase-tab').forEach(function (t, idx) {
      t.classList.toggle('hf-welcome__phase-tab--active', idx === i);
    });
    document.querySelectorAll('.hf-welcome__dot').forEach(function (d, idx) {
      d.classList.toggle('hf-welcome__dot--active', idx === i);
      d.style.background = idx === i ? (d.dataset.colorHex || '') : '';
    });
  }

  var _liquidNavBound = false;
  var _ignoreSectionSelectUntil = 0;
  function bindLiquidNavigation() {
    if (_liquidNavBound) return;
    _liquidNavBound = true;
    // Capture phase so Theme Editor / overlays don't steal the first click
    document.addEventListener('click', function (e) {
      // Never intercept cart drawer / dialog clicks
      if (e.target.closest('cart-drawer-component, dialog')) return;

      // Tracking is independent of navigation: anything carrying data-hf-track
      // is recorded, whether or not it also moves the user to another screen.
      // One bounded event name with the button id as a property — a distinct
      // event name per button would blow past GA4's 500-name limit.
      var tracked = e.target.closest('[data-hf-track]');
      if (tracked) {
        evTrack('Button_Clicked', {
          id: tracked.getAttribute('data-hf-track'),
          from: state.screen || null,
          to: tracked.getAttribute('data-hf-go') || null,
        });
      }

      var btn = e.target.closest('[data-hf-go]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      var screen = btn.getAttribute('data-hf-go');
      if (!screen) return;
      // Don't let theme-editor section:select yank us to login right after CTA
      _ignoreSectionSelectUntil = Date.now() + 800;
      setState({ screen: screen });
    }, true);
  }

  var _welcomeBound = false;
  function bindWelcomeInteractions() {
    if (_welcomeBound) return;
    _welcomeBound = true;

    document.addEventListener('click', function (e) {
      var tab = e.target.closest('.hf-welcome__phase-tab[data-insight-index]');
      if (tab) {
        scrollInsightTo(parseInt(tab.getAttribute('data-insight-index'), 10));
        return;
      }
      var dot = e.target.closest('.hf-welcome__dot[data-insight-index]');
      if (dot) {
        scrollInsightTo(parseInt(dot.getAttribute('data-insight-index'), 10));
      }
    });

    var track = document.getElementById('hf-insight-track');
    if (!track || track._scrollBound) return;
    track._scrollBound = true;

    var scrollTimer;
    track.addEventListener('scroll', function () {
      if (track._scrollLock) return;
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(function () {
        var trackRect = track.getBoundingClientRect();
        var center = trackRect.left + trackRect.width / 2;
        var closest = 0;
        var closestDist = Infinity;
        for (var i = 0; i < track.children.length; i++) {
          var cardRect = track.children[i].getBoundingClientRect();
          var cardCenter = cardRect.left + cardRect.width / 2;
          var dist = Math.abs(cardCenter - center);
          if (dist < closestDist) { closestDist = dist; closest = i; }
        }
        if (closest !== state.welcomeInsight) {
          state.welcomeInsight = closest;
          document.querySelectorAll('.hf-welcome__phase-tab').forEach(function (t, idx) {
            t.classList.toggle('hf-welcome__phase-tab--active', idx === closest);
          });
          document.querySelectorAll('.hf-welcome__dot').forEach(function (d, idx) {
            d.classList.toggle('hf-welcome__dot--active', idx === closest);
            d.style.background = idx === closest ? (d.dataset.colorHex || '') : '';
          });
        }
      }, 50);
    }, { passive: true });
  }

  // ─── SHARED: SVG helper ──────────────────────────────────────
  function svgEl(tag, attrs, ...children) {
    var el = document.createElementNS('http://www.w3.org/2000/svg', tag);
    if (attrs) {
      for (var k in attrs) {
        if (attrs.hasOwnProperty(k)) el.setAttribute(k, attrs[k]);
      }
    }
    for (var i = 0; i < children.length; i++) {
      if (children[i] == null) continue;
      if (typeof children[i] === 'string') {
        el.appendChild(document.createTextNode(children[i]));
      } else if (children[i] instanceof Node) {
        el.appendChild(children[i]);
      }
    }
    return el;
  }

  // ─── SHARED: Grade Ring ─────────────────────────────────────
  function renderGradeRing(score, size) {
    var sz = size || 120;
    var radius = (sz / 2) - 8;
    var circumference = 2 * Math.PI * radius;
    var g = getGrade(score);
    var offset = circumference - (score / 100) * circumference;

    var svg = svgEl('svg', { width: String(sz), height: String(sz), viewBox: '0 0 ' + sz + ' ' + sz });
    var cx = String(sz / 2);
    var cy = String(sz / 2);

    // Background circle
    svg.appendChild(svgEl('circle', {
      cx: cx, cy: cy, r: String(radius),
      fill: 'none', stroke: '#f0f0f0', 'stroke-width': '6'
    }));

    // Score arc
    var arc = svgEl('circle', {
      cx: cx, cy: cy, r: String(radius),
      fill: 'none', stroke: g.ringColor, 'stroke-width': '6',
      'stroke-linecap': 'round',
      'stroke-dasharray': String(circumference),
      'stroke-dashoffset': String(circumference),
      transform: 'rotate(-90 ' + cx + ' ' + cy + ')'
    });
    svg.appendChild(arc);

    // Grade letter
    var txt = svgEl('text', {
      x: cx, y: cy,
      'text-anchor': 'middle', 'dominant-baseline': 'central',
      fill: g.color, 'font-size': String(Math.round(sz * 0.35)),
      'font-weight': '800', 'font-family': 'inherit'
    }, g.grade);
    svg.appendChild(txt);

    // Wrap in div for animation
    var wrapper = h('div', { className: 'hf-grade-ring', style: { display: 'inline-block' } });
    wrapper.appendChild(svg);

    // Animate on next frame
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        arc.setAttribute('stroke-dashoffset', String(offset));
        arc.style.transition = 'stroke-dashoffset 1s ease-out';
      });
    });

    return wrapper;
  }

  // ─── SHARED: Femmi character ────────────────────────────────
  function renderFemmi(mood, color, size) {
    var sz = size || 48;
    var moodIcons = {
      happy: 'heart', tired: 'moon', confident: 'bolt',
      sensitive: 'flower', energised: 'bolt', curious: 'brain'
    };
    var iconName = moodIcons[mood] || 'heart';
    return h('div', {
      className: 'hf-femmi',
      style: {
        width: sz + 'px', height: sz + 'px', borderRadius: '50%',
        background: color || '#FBEAF0', display: 'flex',
        alignItems: 'center', justifyContent: 'center', flexShrink: '0'
      }
    }, makeIcon(iconName, color ? '#fff' : '#0C2B82', Math.round(sz * 0.45)));
  }

  // ─── SHARED: Bottom Nav ─────────────────────────────────────
  // ─── SVG ICON LIBRARY ────────────────────────────────────────
  // Reusable inline SVG icons (24×24, stroke-based)
  function makeIcon(name, color, size) {
    var s = size || 20;
    var base = { viewBox: '0 0 24 24', width: String(s), height: String(s), fill: 'none', stroke: color || '#D4537E', 'stroke-width': '2', 'stroke-linecap': 'round', 'stroke-linejoin': 'round' };
    switch (name) {
      case 'heart':
        return svgEl('svg', base, svgEl('path', { d: 'M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z' }));
      case 'calendar':
        return svgEl('svg', base, svgEl('rect', { x: '3', y: '4', width: '18', height: '18', rx: '2' }), svgEl('line', { x1: '16', y1: '2', x2: '16', y2: '6' }), svgEl('line', { x1: '8', y1: '2', x2: '8', y2: '6' }), svgEl('line', { x1: '3', y1: '10', x2: '21', y2: '10' }));
      case 'bolt':
        return svgEl('svg', base, svgEl('path', { d: 'M13 2L3 14h9l-1 8 10-12h-9l1-8z', fill: color || '#D4537E', stroke: 'none' }));
      case 'drop':
        return svgEl('svg', base, svgEl('path', { d: 'M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0L12 2.69z' }));
      case 'clock':
        return svgEl('svg', base, svgEl('circle', { cx: '12', cy: '12', r: '10' }), svgEl('path', { d: 'M12 6v6l4 2' }));
      case 'brain':
        return svgEl('svg', base, svgEl('path', { d: 'M12 2a7 7 0 0 0-7 7c0 3 2 5.5 4 7.5L12 20l3-3.5c2-2 4-4.5 4-7.5a7 7 0 0 0-7-7z' }), svgEl('path', { d: 'M9 10h6' }), svgEl('path', { d: 'M12 7v6' }));
      case 'moon':
        return svgEl('svg', base, svgEl('path', { d: 'M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z' }));
      case 'pill':
        return svgEl('svg', base, svgEl('path', { d: 'M10.5 1.5l-8 8a5 5 0 0 0 7.07 7.07l8-8a5 5 0 0 0-7.07-7.07z' }), svgEl('line', { x1: '7.5', y1: '10.5', x2: '13.5', y2: '4.5' }));
      case 'chart':
        return svgEl('svg', base, svgEl('path', { d: 'M18 20V10' }), svgEl('path', { d: 'M12 20V4' }), svgEl('path', { d: 'M6 20v-6' }));
      case 'alert':
        return svgEl('svg', base, svgEl('circle', { cx: '12', cy: '12', r: '10' }), svgEl('line', { x1: '12', y1: '8', x2: '12', y2: '12' }), svgEl('line', { x1: '12', y1: '16', x2: '12.01', y2: '16' }));
      case 'cycle':
        return svgEl('svg', base, svgEl('path', { d: 'M21.5 2v6h-6' }), svgEl('path', { d: 'M2.5 22v-6h6' }), svgEl('path', { d: 'M2.5 11.5a10 10 0 0 1 16.6-5.1L21.5 8' }), svgEl('path', { d: 'M21.5 12.5a10 10 0 0 1-16.6 5.1L2.5 16' }));
      case 'shield':
        return svgEl('svg', base, svgEl('path', { d: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z' }));
      case 'star':
        return svgEl('svg', base, svgEl('path', { d: 'M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 21 12 17.27 5.82 21 7 14.14l-5-4.87 6.91-1.01L12 2z', fill: color || '#D4537E', 'stroke-width': '0' }));
      case 'check':
        return svgEl('svg', base, svgEl('path', { d: 'M20 6L9 17l-5-5' }));
      case 'trophy':
        return svgEl('svg', base, svgEl('path', { d: 'M6 9H3a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h3' }), svgEl('path', { d: 'M18 9h3a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1h-3' }), svgEl('path', { d: 'M6 4h12v5a6 6 0 0 1-12 0V4z' }), svgEl('path', { d: 'M9 18h6' }), svgEl('path', { d: 'M12 14v4' }), svgEl('path', { d: 'M8 22h8' }));
      case 'trending':
        return svgEl('svg', base, svgEl('path', { d: 'M23 6l-9.5 9.5-5-5L1 18' }), svgEl('path', { d: 'M17 6h6v6' }));
      case 'target':
        return svgEl('svg', base, svgEl('circle', { cx: '12', cy: '12', r: '10' }), svgEl('circle', { cx: '12', cy: '12', r: '6' }), svgEl('circle', { cx: '12', cy: '12', r: '2' }));
      case 'flower':
        return svgEl('svg', base, svgEl('circle', { cx: '12', cy: '12', r: '3', fill: color || '#0C2B82', stroke: 'none' }),
          svgEl('path', { d: 'M12 2a4 4 0 0 0 0 8 4 4 0 0 0 0-8z', fill: (color || '#0C2B82') + '30', stroke: color || '#0C2B82', 'stroke-width': '1' }),
          svgEl('path', { d: 'M19.07 4.93a4 4 0 0 0-5.66 5.66 4 4 0 0 0 5.66-5.66z', fill: (color || '#0C2B82') + '30', stroke: color || '#0C2B82', 'stroke-width': '1' }),
          svgEl('path', { d: 'M22 12a4 4 0 0 0-8 0 4 4 0 0 0 8 0z', fill: (color || '#0C2B82') + '30', stroke: color || '#0C2B82', 'stroke-width': '1' })
        );
      default:
        return svgEl('svg', base, svgEl('circle', { cx: '12', cy: '12', r: '10' }));
    }
  }

  // SVG icon paths for bottom nav (24×24 viewBox, stroke-based)
  var NAV_ICONS = {
    home: function (color) {
      return svgEl('svg', { viewBox: '0 0 24 24', width: '22', height: '22', fill: 'none', stroke: color, 'stroke-width': '1.8', 'stroke-linecap': 'round', 'stroke-linejoin': 'round' },
        svgEl('path', { d: 'M3 10.5L12 3l9 7.5V21a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V10.5z' }),
        svgEl('path', { d: 'M9 22V13h6v9' })
      );
    },
    log: function (color) {
      return svgEl('svg', { viewBox: '0 0 24 24', width: '22', height: '22', fill: 'none', stroke: color, 'stroke-width': '1.8', 'stroke-linecap': 'round', 'stroke-linejoin': 'round' },
        svgEl('rect', { x: '3', y: '4', width: '18', height: '18', rx: '2' }),
        svgEl('line', { x1: '16', y1: '2', x2: '16', y2: '6' }),
        svgEl('line', { x1: '8', y1: '2', x2: '8', y2: '6' }),
        svgEl('line', { x1: '3', y1: '10', x2: '21', y2: '10' }),
        svgEl('line', { x1: '8', y1: '14', x2: '12', y2: '14' }),
        svgEl('line', { x1: '8', y1: '18', x2: '16', y2: '18' })
      );
    },
    score: function (color) {
      return svgEl('svg', { viewBox: '0 0 24 24', width: '22', height: '22', fill: 'none', stroke: color, 'stroke-width': '1.8', 'stroke-linecap': 'round', 'stroke-linejoin': 'round' },
        svgEl('path', { d: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z' }),
        svgEl('path', { d: 'M12 6v6l4 2' })
      );
    },
    calendar: function (color) {
      return svgEl('svg', { viewBox: '0 0 24 24', width: '22', height: '22', fill: 'none', stroke: color, 'stroke-width': '1.8', 'stroke-linecap': 'round', 'stroke-linejoin': 'round' },
        svgEl('rect', { x: '3', y: '4', width: '18', height: '18', rx: '2' }),
        svgEl('line', { x1: '16', y1: '2', x2: '16', y2: '6' }),
        svgEl('line', { x1: '8', y1: '2', x2: '8', y2: '6' }),
        svgEl('line', { x1: '3', y1: '10', x2: '21', y2: '10' })
      );
    },
    doctors: function (color) {
      return svgEl('svg', { viewBox: '0 0 24 24', width: '22', height: '22', fill: 'none', stroke: color, 'stroke-width': '1.8', 'stroke-linecap': 'round', 'stroke-linejoin': 'round' },
        svgEl('path', { d: 'M4.8 2.3A1 1 0 0 0 4 3.3v7.4a5 5 0 0 0 5 5h1v4a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1v-4h1a5 5 0 0 0 5-5V3.3a1 1 0 0 0-.8-1' }),
        svgEl('line', { x1: '12', y1: '8', x2: '12', y2: '12' }),
        svgEl('line', { x1: '10', y1: '10', x2: '14', y2: '10' })
      );
    },
    pcos: function (color) {
      return svgEl('svg', { viewBox: '0 0 24 24', width: '22', height: '22', fill: 'none', stroke: color, 'stroke-width': '1.8', 'stroke-linecap': 'round', 'stroke-linejoin': 'round' },
        svgEl('circle', { cx: '12', cy: '12', r: '4' }),
        svgEl('path', { d: 'M12 2v4' }),
        svgEl('path', { d: 'M12 18v4' }),
        svgEl('path', { d: 'M4.93 4.93l2.83 2.83' }),
        svgEl('path', { d: 'M16.24 16.24l2.83 2.83' }),
        svgEl('path', { d: 'M2 12h4' }),
        svgEl('path', { d: 'M18 12h4' }),
        svgEl('path', { d: 'M4.93 19.07l2.83-2.83' }),
        svgEl('path', { d: 'M16.24 7.76l2.83-2.83' })
      );
    }
  };

  // Transient bottom toast.
  function showToast(msg) {
    var existing = document.getElementById('hf-soon-toast');
    if (existing) existing.remove();
    var t = document.createElement('div');
    t.id = 'hf-soon-toast';
    t.textContent = msg;
    t.style.cssText = 'position:fixed;left:50%;bottom:86px;transform:translateX(-50%);background:#8B1A4A;color:#fff;padding:11px 20px;border-radius:999px;font:600 13px -apple-system,BlinkMacSystemFont,sans-serif;z-index:300;box-shadow:0 10px 28px rgba(139,26,74,.38);opacity:0;transition:opacity .2s ease, transform .2s ease;max-width:80vw;text-align:center;';
    document.body.appendChild(t);
    requestAnimationFrame(function () { t.style.opacity = '1'; t.style.transform = 'translateX(-50%) translateY(-4px)'; });
    setTimeout(function () { t.style.opacity = '0'; setTimeout(function () { if (t.parentNode) t.remove(); }, 250); }, 1700);
  }
  function showComingSoon(label) { showToast((label || 'This') + ' is coming soon ✨'); }

  // ─── PRODUCTS / ADD-TO-CART (Shopify AJAX Cart API) ─────────
  // Adds the variant via /cart/add.js and applies the product's preconfigured
  // discount code via /discount/CODE (sets the session discount — confirmed to
  // persist and surface in /cart.js items[].discounts). Then opens a custom
  // side cart (the theme's native drawer is hidden on this full-screen page).
  function money(paise) {
    var n = (Number(paise) || 0) / 100;
    return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 });
  }
  // The Horizon cart drawer lives in the header section (which this page hides).
  function getHeaderSectionId() {
    var el = document.querySelector('[id^="shopify-section"][id*="header" i]');
    return el ? el.id.replace('shopify-section-', '') : null;
  }

  var _cartAddInFlight = false;

  // Apply a discount the same way the bag "Apply" button does.
  // GET /discount/CODE is a navigation URL — fetch() often 401s / no-ops, so
  // FAB10 looked random. /cart/update.js is the reliable Cart Ajax API.
  function applyCartDiscount(code, sectionIds) {
    var trimmed = (code || '').trim();
    if (!trimmed) return Promise.resolve(null);

    var existing = Array.from(document.querySelectorAll('.cart-discount__pill'))
      .map(function (el) { return el.getAttribute('data-discount-code') || ''; })
      .filter(Boolean);
    if (existing.some(function (c) { return c.toLowerCase() === trimmed.toLowerCase(); })) {
      return Promise.resolve(null); // already on cart
    }

    var url = (window.Theme && Theme.routes && Theme.routes.cart_update_url) || '/cart/update.js';
    var body = {
      discount: existing.concat([trimmed]).join(','),
    };
    if (sectionIds && sectionIds.length) body.sections = sectionIds;

    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (res) {
      if (!res.ok) throw new Error('discount apply failed');
      return res.json();
    }).catch(function () {
      return null; // add still succeeded; drawer opens without coupon
    });
  }

  function cartAdd(product) {
    if (!product || !product.variantId || _cartAddInFlight) return;
    _cartAddInFlight = true;
    evTrack('Product_AddedToCart', {
      productId: product.handle, variantId: product.variantId,
      handle: product.handle, discountCode: product.discountCode || null,
      title: product.title || null, image: product.image || null, url: product.url || null,
    });
    showToast('Adding to cart…');
    var headerId = getHeaderSectionId();
    var sectionIds = headerId ? [headerId] : [];

    // 1) add item, 2) apply preconfigured coupon via cart/update.js, 3) tell the
    // theme a cart update happened — cart-drawer.js opens itself on 'cart:update'
    // (its own document listener) and cart-items-component morphs the header
    // section from detail.data.sections, exactly like a normal product-form add.
    // No manual drawer cloning/opening here anymore.
    fetch('/cart/add.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: product.variantId, quantity: 1 }],
        sections: sectionIds.length ? sectionIds : undefined,
        sections_url: location.pathname,
      }),
    }).then(function (r) {
      if (!r.ok) throw new Error('add failed');
      return r.json();
    }).then(function (addJson) {
      return applyCartDiscount(product.discountCode, sectionIds).then(function (discJson) {
        return { addJson: addJson, discJson: discJson };
      });
    }).then(function (bundle) {
      var cartData = bundle.discJson || bundle.addJson;
      var headerHtml = null;
      if (headerId) {
        if (bundle.discJson && bundle.discJson.sections && bundle.discJson.sections[headerId]) {
          headerHtml = bundle.discJson.sections[headerId];
        } else if (bundle.addJson.sections && bundle.addJson.sections[headerId]) {
          headerHtml = bundle.addJson.sections[headerId];
        }
      }

      if (headerId && !headerHtml) {
        // Section HTML missing from the add/discount response — refetch it so
        // the drawer morphs in the new item instead of opening on stale content.
        return fetch(location.pathname + '?sections=' + encodeURIComponent(headerId))
          .then(function (r) { return r.json(); })
          .then(function (j) {
            var sections = {}; sections[headerId] = j[headerId];
            fireCartUpdateEvent(cartData, sections);
          })
          .catch(function () { fireCartUpdateEvent(cartData, {}); });
      }
      var sections = {};
      if (headerId && headerHtml) sections[headerId] = headerHtml;
      fireCartUpdateEvent(cartData, sections);
    }).catch(function () {
      var cart = (window.__fabscore && window.__fabscore.cartUrl) || '/cart';
      window.location.href = product.discountCode ? cart + '?discount=' + encodeURIComponent(product.discountCode) : cart;
    }).then(function () { _cartAddInFlight = false; });
  }

  // Dispatch the theme's own 'cart:update' event so cart-drawer.js opens the
  // drawer and cart-items-component morphs it — the same mechanism a normal
  // product-form add-to-cart uses. Keeps FabScore's cart behavior identical
  // to the rest of the storefront instead of a separate implementation.
  function fireCartUpdateEvent(cartData, sections) {
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cartData,
        sourceId: 'fabscore-recs',
        data: {
          didError: false,
          source: 'fabscore-recs',
          itemCount: cartData && cartData.item_count,
          sections: sections || {},
        },
      },
    }));
  }


  function renderProductCard(p) {
    var card = h('div', { style: { display: 'flex', gap: '12px', alignItems: 'center', background: '#fff', border: '1.5px solid #F1DCE6', borderRadius: '16px', padding: '12px', marginBottom: '10px', boxShadow: '0 8px 20px -12px rgba(212,83,126,0.3)' } });
    if (p.image) card.appendChild(h('img', { src: p.image, alt: p.title || '', style: { width: '62px', height: '62px', objectFit: 'cover', borderRadius: '12px', flexShrink: '0' } }));
    var mid = h('div', { style: { flex: '1', minWidth: '0' } });
    if (p.badge) mid.appendChild(h('span', { style: { display: 'inline-block', background: '#FBEAF1', color: '#8B1A4A', fontSize: '10px', fontWeight: '800', padding: '2px 8px', borderRadius: '999px', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '.03em' } }, p.badge));
    mid.appendChild(h('p', { style: { fontSize: '14px', fontWeight: '700', color: '#111', margin: '0 0 2px', lineHeight: '1.3', overflow: 'hidden', textOverflow: 'ellipsis' } }, p.title || ''));
    mid.appendChild(h('p', { style: { fontSize: '13px', color: '#666', margin: '0' }, innerHTML: (p.priceFormatted || '') + (p.discountCode ? ' · <span style="color:#1D9E75;font-weight:700">coupon at cart</span>' : '') }));
    card.appendChild(mid);
    card.appendChild(h('button', {
      className: 'hf-btn', disabled: p.available === false ? 'disabled' : null,
      style: { width: 'auto', padding: '11px 16px', borderRadius: '12px', fontSize: '13px', fontWeight: '800', flexShrink: '0' },
      onClick: function () { cartAdd(p); }
    }, p.available === false ? 'Sold out' : 'Add'));
    return card;
  }

  // Recommended products for a given score (filtered by each block's score range).
  function renderProductRecs(score) {
    var all = (window.__fabscore && window.__fabscore.products) || [];
    var list = all.filter(function (p) {
      if (!p || !p.variantId) return false;
      if (score == null) return true;
      return score >= (p.scoreMin || 0) && score <= (p.scoreMax == null ? 100 : p.scoreMax);
    });
    if (!list.length) return null;
    list.forEach(function (p) { evTrack('Product_Recommended', { handle: p.handle, productId: p.handle }); });
    var wrap = h('div', { style: { padding: '0 16px 16px' } },
      h('h3', { style: { fontSize: '15px', fontWeight: '800', color: '#111', margin: '18px 0 12px' } }, 'Recommended for you')
    );
    list.forEach(function (p) { wrap.appendChild(renderProductCard(p)); });
    return wrap;
  }

  function renderBottomNav(activeScreen) {
    // A check-in is pending when the user has a profile, hasn't logged today,
    // and today maps to a touchpoint.
    var pending = false;
    var navProfile = state.profile;
    if (navProfile && navProfile.lastPeriod && !state.todayLog) {
      var navDay = getDayOfCycle(navProfile.lastPeriod, navProfile.cycleLength);
      pending = !!getTouchpoint(navDay, state.history || [], navProfile);
    }
    var items = [
      { id: 'checkin', label: 'Home', iconKey: 'home' },
      { id: 'calculator', label: 'Calendar', iconKey: 'calendar' },
      { id: 'score', label: 'Score', iconKey: 'score' },
      { id: 'checkin', label: 'Doctors', iconKey: 'doctors', soon: true },
      { id: 'checkin', label: 'PCOS', iconKey: 'pcos', soon: true },
    ];
    return h('div', {
      className: 'hf-bottom-nav'
    },
      ...items.map(function (item) {
        // Only highlight the primary nav item for the active screen.
        var isActive = false;
        if (activeScreen === 'checkin' && item.iconKey === 'home') isActive = true;
        else if (activeScreen === 'calculator' && item.iconKey === 'calendar') isActive = true;
        else if (activeScreen === 'score' && item.iconKey === 'score') isActive = true;

        var color = isActive ? '#D4537E' : '#B0B0B0';
        var iconFn = NAV_ICONS[item.iconKey];
        var iconSvg = iconFn ? iconFn(color) : null;

        var btn = h('button', {
          className: 'hf-bottom-nav__item' + (isActive ? ' hf-bottom-nav__item--active' : ''),
          style: item.soon ? { position: 'relative', opacity: '0.8' } : { position: 'relative' },
          onClick: function () {
            if (item.soon) {
              evTrack('ComingSoon_Clicked', { feature: item.label });
              showComingSoon(item.label);
            } else {
              setState({ screen: item.id });
            }
          }
        });
        // Icon wrapper
        var iconWrap = h('div', { className: 'hf-bottom-nav__icon-wrap' });
        if (iconSvg) iconWrap.appendChild(iconSvg);
        btn.appendChild(iconWrap);
        // "Soon" badge for upcoming features
        if (item.soon) {
          btn.appendChild(h('span', {
            style: {
              position: 'absolute', top: '2px', right: '10px',
              background: 'linear-gradient(135deg,#FF7FB0,#D4537E)', color: '#fff',
              fontSize: '7px', fontWeight: '800', letterSpacing: '.04em', textTransform: 'uppercase',
              padding: '1px 4px', borderRadius: '6px', lineHeight: '1.3',
              boxShadow: '0 2px 6px rgba(212,83,126,0.4)'
            }
          }, 'Soon'));
        }
        // Pending check-in dot on the Home entry point
        if (pending && item.id === 'checkin' && item.iconKey === 'home') {
          btn.appendChild(h('span', {
            style: {
              position: 'absolute', top: '4px', right: '50%', marginRight: '-14px',
              width: '8px', height: '8px', borderRadius: '50%', background: '#D4537E',
              boxShadow: '0 0 0 2px #fff'
            }
          }));
        }
        // Label
        btn.appendChild(h('span', { className: 'hf-bottom-nav__label' + (isActive ? ' hf-bottom-nav__label--active' : '') }, item.label));
        // Active dot
        if (isActive) {
          btn.appendChild(h('div', { className: 'hf-bottom-nav__dot' }));
        }
        return btn;
      })
    );
  }

  function doLogout() {
    fabscoreAPI.logout().then(function () {
      state.customer = { loggedIn: false };
      state.profile = null;
      state.todayLog = null;
      state.history = [];
      state.walletBalance = null;
      state.predictions = null;
      state.onboardStep = 0;
      state.onboardData = {};
      _onbAnswered = {};
      state.onboardSaving = false;
      state.checkinAnswers = {};
      setState({ screen: 'login', loginStep: 'phone' });
    });
  }

  function renderLogoutBar() {
    return h('div', { className: 'hf-logout-bar' },
      h('button', {
        type: 'button',
        className: 'hf-logout-btn',
        onClick: doLogout
      }, 'Log out')
    );
  }

  // ─── SHARED: Earn Toast ─────────────────────────────────────
  function renderEarnToast() {
    if (!state.earnResult) return null;
    var toast = h('div', {
      className: 'hf-earn-toast',
      style: {
        position: 'fixed', top: '16px', left: '50%', transform: 'translateX(-50%)',
        background: '#8B1A4A', color: '#fff', padding: '12px 24px',
        borderRadius: '12px', fontSize: '15px', fontWeight: '700',
        zIndex: '200', boxShadow: '0 8px 32px rgba(26,17,80,.3)',
        display: 'flex', alignItems: 'center', gap: '8px'
      }
    },
      h('span', { style: { fontSize: '14px' } }, '+'),
      h('span', null, '+' + (state.earnResult.earned || 0) + ' points')
    );
    setTimeout(function () {
      setState({ earnResult: null });
    }, 3000);
    return toast;
  }

  // ─── SHARED: Mini Calendar ──────────────────────────────────
  function renderMiniCalendar(selectedDate, onSelect) {
    var today = new Date();
    var viewMonth = (state.calendarMonth != null) ? state.calendarMonth : today.getMonth();
    var viewYear = (state.calendarYear != null) ? state.calendarYear : today.getFullYear();

    var monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    var dayLabels = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

    var firstDay = new Date(viewYear, viewMonth, 1).getDay();
    var daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

    // Calculate min date (6 months ago)
    var minDate = new Date(today);
    minDate.setMonth(minDate.getMonth() - 6);

    function canGoBack() {
      return viewYear > minDate.getFullYear() || (viewYear === minDate.getFullYear() && viewMonth > minDate.getMonth());
    }
    function canGoForward() {
      return viewYear < today.getFullYear() || (viewYear === today.getFullYear() && viewMonth < today.getMonth());
    }

    var cal = h('div', { className: 'hf-calendar', style: { background: '#fff', borderRadius: '16px', border: '1px solid #f0f0f0', padding: '16px' } },
      // Month navigation
      h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' } },
        h('button', {
          type: 'button',
          style: { background: 'none', border: 'none', fontSize: '18px', cursor: canGoBack() ? 'pointer' : 'default', padding: '4px 8px', opacity: canGoBack() ? '1' : '0.3' },
          onClick: function () {
            if (!canGoBack()) return;
            var nm = viewMonth - 1;
            var ny = viewYear;
            if (nm < 0) { nm = 11; ny--; }
            setState({ calendarMonth: nm, calendarYear: ny });
          }
        }, '‹'),
        h('span', { style: { fontWeight: '700', fontSize: '15px', color: '#8B1A4A' } }, monthNames[viewMonth] + ' ' + viewYear),
        h('button', {
          type: 'button',
          style: { background: 'none', border: 'none', fontSize: '18px', cursor: canGoForward() ? 'pointer' : 'default', padding: '4px 8px', opacity: canGoForward() ? '1' : '0.3' },
          onClick: function () {
            if (!canGoForward()) return;
            var nm = viewMonth + 1;
            var ny = viewYear;
            if (nm > 11) { nm = 0; ny++; }
            setState({ calendarMonth: nm, calendarYear: ny });
          }
        }, '›')
      ),
      // Day headers
      h('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', textAlign: 'center', marginBottom: '4px' } },
        ...dayLabels.map(function (d) {
          return h('div', { style: { fontSize: '11px', color: '#999', fontWeight: '600', padding: '4px 0' } }, d);
        })
      ),
      // Day grid
      h('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', textAlign: 'center' } },
        // Empty cells for offset
        ...Array.from({ length: firstDay }, function () {
          return h('div', { style: { padding: '8px 0' } });
        }),
        // Day cells
        ...Array.from({ length: daysInMonth }, function (_, i) {
          var dayNum = i + 1;
          var dateStr = viewYear + '-' + String(viewMonth + 1).padStart(2, '0') + '-' + String(dayNum).padStart(2, '0');
          var dateObj = new Date(viewYear, viewMonth, dayNum);
          var isFuture = dateObj > today;
          var isPast = dateObj < minDate;
          var isSelected = selectedDate === dateStr;
          var isToday = dateStr === TODAY;
          var disabled = isFuture || isPast;

          return h('button', {
            type: 'button',
            style: {
              padding: '8px 0', border: 'none', borderRadius: '8px', cursor: disabled ? 'default' : 'pointer',
              fontSize: '13px', fontWeight: isSelected ? '700' : '500',
              background: isSelected ? '#8B1A4A' : isToday ? '#f0f0f0' : 'transparent',
              color: isSelected ? '#fff' : disabled ? '#ccc' : '#8B1A4A',
            },
            onClick: function () {
              if (!disabled && onSelect) onSelect(dateStr);
            }
          }, String(dayNum));
        })
      )
    );
    return cal;
  }

  // ─── SCREEN: ONBOARDING ─────────────────────────────────────
  // Onboarding reports two different things, so it emits two events.
  //
  // Onboarding_QuestionAnswered fires once, the first time a field is touched,
  // and says where people stall — including people who then abandon the step.
  // It must stay once-only: `name` is tracked on every keystroke, so dropping
  // the guard would emit one event per character typed.
  //
  // Onboarding_AnswerSubmitted fires on Continue and carries what was actually
  // chosen. Reading the choice off the first touch instead would report a
  // multi-select's first tick as the whole answer, and would miss anyone who
  // changed their mind before submitting.
  //
  // Two fields are never eligible for an answer whatever the setting says:
  // `name` is PII and `lastPeriod` is a raw menstrual date, and neither has a
  // bounded option set to report in the first place. Multi-selects report how
  // many options were picked, not which — the picks themselves (symptoms,
  // PCOS) stay in the first-party pipeline.
  var ONB_ANSWER_NEVER = { name: true, lastPeriod: true };
  var _onbAnswered = {};
  function trackOnboardField(key, val) {
    var filled = Array.isArray(val) ? val.length > 0 : (val != null && val !== '' && val !== false);
    if (!filled) return;
    var stepNo = state.onboardStep || 0;
    var seen = stepNo + ':' + key;
    if (_onbAnswered[seen]) return;
    _onbAnswered[seen] = true;
    evTrack('Onboarding_QuestionAnswered', { index: stepNo, field: toEventName(key) });
  }

  // One event per field the user actually touched on this step — the touched
  // set is already in _onbAnswered, so which fields belong to which step never
  // has to be restated here. Going Back and submitting a step again fires
  // again; that is a second submission, and the later event carries the
  // revised answer.
  function trackOnboardAnswers(step) {
    var allowed = pixelConfig().onbAnswers;
    var data = state.onboardData || {};
    var prefix = step + ':';
    Object.keys(_onbAnswered).forEach(function (seen) {
      if (seen.indexOf(prefix) !== 0) return;
      var key = seen.slice(prefix.length);
      if (ONB_ANSWER_NEVER[key] || !allowed[key]) return;
      var val = data[key];
      if (val == null || val === '') return;
      // `answer`, not `value` — GA4 reserves `value` for monetary amounts.
      function emit(answer) {
        evTrack('Onboarding_AnswerSubmitted', { index: step, field: toEventName(key), answer: answer });
      }
      if (!Array.isArray(val)) { emit(val); return; }
      if (allowed[key] !== 'values') { emit(val.length); return; }
      val.forEach(function (v) { emit(toAnswerValue(v)); });
    });
  }

  function renderOnboarding() {
    var step = state.onboardStep || 0;
    var data = state.onboardData || {};
    var totalSteps = 3;

    function updateData(key, val) {
      var nd = Object.assign({}, data);
      nd[key] = val;
      trackOnboardField(key, val);
      setState({ onboardData: nd });
    }

    function toggleMulti(key, val) {
      var nd = Object.assign({}, data);
      var arr = (nd[key] || []).slice();
      var idx = arr.indexOf(val);
      if (idx === -1) arr.push(val); else arr.splice(idx, 1);
      nd[key] = arr;
      trackOnboardField(key, arr);
      setState({ onboardData: nd });
    }

    // Supplements only: "None" is exclusive — picking it clears others;
    // picking any supplement clears "None".
    function toggleSupplements(val) {
      var nd = Object.assign({}, data);
      var arr = (nd.supplements || []).slice();
      if (val === 'None') {
        nd.supplements = arr.indexOf('None') !== -1 ? [] : ['None'];
      } else {
        arr = arr.filter(function (s) { return s !== 'None'; });
        var idx = arr.indexOf(val);
        if (idx === -1) arr.push(val); else arr.splice(idx, 1);
        nd.supplements = arr;
      }
      trackOnboardField('supplements', nd.supplements);
      setState({ onboardData: nd });
    }

    // Progress bar
    var progress = h('div', { style: { padding: '16px 22px 0' } },
      h('div', { style: { display: 'flex', gap: '6px' } },
        ...Array.from({ length: totalSteps }, function (_, i) {
          return h('div', {
            style: {
              flex: '1', height: '4px', borderRadius: '2px',
              background: i <= step ? '#8B1A4A' : '#E8E8E8',
              transition: 'background .3s'
            }
          });
        })
      ),
      h('p', { style: { fontSize: '12px', color: '#999', margin: '8px 0 0' } }, 'Step ' + (step + 1) + ' of ' + totalSteps)
    );

    var content = null;

    // ── Step 0: Name, Age, Consent ──
    if (step === 0) {
      var urlName = (state.urlParams && state.urlParams.name) || '';
      if (!data.name && urlName) {
        data.name = urlName.charAt(0).toUpperCase() + urlName.slice(1).toLowerCase();
      }
      var ageGroups = ['18-22', '23-27', '28-32', '33-38', '39+'];

      content = h('div', { style: { padding: '24px 22px' } },
        h('h2', { style: { fontSize: '20px', fontWeight: '800', color: '#8B1A4A', margin: '0 0 4px' } }, 'Let\'s get to know you'),
        h('p', { style: { fontSize: '13px', color: '#666', margin: '0 0 20px' } }, 'This stays private. Always.'),

        // Name
        h('label', { style: { fontSize: '13px', fontWeight: '600', color: '#8B1A4A', display: 'block', marginBottom: '6px' } }, 'Your name'),
        h('input', {
          type: 'text', value: data.name || '',
          placeholder: 'First name',
          id: 'hf-onboard-name',
          className: 'hf-input',
          style: {
            width: '100%', padding: '14px 16px', borderRadius: '12px',
            border: '1.5px solid #E8E8E8', fontSize: '15px', marginBottom: '20px',
            boxSizing: 'border-box', outline: 'none'
          },
          // Write directly on input (no setState → no re-render). Avoids a blur-
          // triggered re-render that would "eat" the first click on an option.
          onInput: function (e) {
            state.onboardData.name = e.target.value;
            trackOnboardField('name', e.target.value);
          }
        }),

        // Age group
        h('label', { style: { fontSize: '13px', fontWeight: '600', color: '#8B1A4A', display: 'block', marginBottom: '8px' } }, 'Age group'),
        h('div', { style: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '24px' } },
          ...ageGroups.map(function (ag) {
            var selected = data.ageGroup === ag;
            return h('button', {
              style: {
                padding: '10px 18px', borderRadius: '10px', border: '1.5px solid ' + (selected ? '#8B1A4A' : '#E8E8E8'),
                background: selected ? '#8B1A4A' : '#fff', color: selected ? '#fff' : '#8B1A4A',
                fontSize: '14px', fontWeight: '600', cursor: 'pointer'
              },
              onClick: function () { updateData('ageGroup', ag); }
            }, ag);
          })
        ),

        // DPDP Consent
        h('div', { style: { background: '#FAFAFA', borderRadius: '12px', padding: '16px', marginBottom: '20px' } },
          h('p', { style: { fontSize: '12px', fontWeight: '700', color: '#8B1A4A', margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: '.05em' } }, 'Data Protection (DPDP Act)'),
          ...[
            { key: 'consent_health', label: 'I consent to processing my health data for cycle tracking' },
            { key: 'consent_age', label: 'I confirm I am 18 years or older' },
            { key: 'consent_private', label: 'I consent to my anonymised check-in data being used to improve the product' },
          ].map(function (c) {
            var checked = !!data[c.key];
            return h('label', {
              style: { display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '10px', cursor: 'pointer', fontSize: '13px', color: '#444', lineHeight: '1.4' },
              onClick: function () { updateData(c.key, !checked); }
            },
              h('div', {
                style: {
                  width: '20px', height: '20px', borderRadius: '6px', flexShrink: '0', marginTop: '1px',
                  border: '1.5px solid ' + (checked ? '#D4537E' : '#ccc'),
                  background: checked ? '#D4537E' : '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }
              }, checked ? makeIcon('check', '#fff', 12) : null),
              h('span', null, c.label)
            );
          })
        )
      );
    }

    // ── Step 1: Cycle Data ──
    if (step === 1) {
      var periodDays = [1, 2, 3, 4, 5, 6, '7+'];
      var cycleLengthOptions = [
        { label: '<20 days', value: 19 },
        { label: '21–24 days', value: 22 },
        { label: '25–27 days', value: 26 },
        { label: '28 days', value: 28 },
        { label: '29 days', value: 29 },
        { label: '30 days', value: 30 },
        { label: '31–34 days', value: 32 },
        { label: '35+ days', value: 35 },
      ];

      content = h('div', { style: { padding: '24px 22px' } },
        h('h2', { style: { fontSize: '20px', fontWeight: '800', color: '#8B1A4A', margin: '0 0 4px' } }, 'Your cycle basics'),
        h('p', { style: { fontSize: '13px', color: '#666', margin: '0 0 20px' } }, 'Best guess is totally fine. We refine over time.'),

        // Last period date
        h('label', { style: { fontSize: '13px', fontWeight: '600', color: '#8B1A4A', display: 'block', marginBottom: '8px' } }, 'When did your last period start?'),
        renderMiniCalendar(data.lastPeriod || null, function (dateStr) {
          updateData('lastPeriod', dateStr);
        }),

        // Period length
        h('label', { style: { fontSize: '13px', fontWeight: '600', color: '#8B1A4A', display: 'block', margin: '20px 0 8px' } }, 'How many days does your period last?'),
        h('div', { style: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' } },
          ...periodDays.map(function (d) {
            var isPlus = d === '7+';
            var stored = isPlus ? 7 : d;
            var selected = Number(data.periodDays) === stored;
            return h('button', {
              type: 'button',
              style: {
                padding: '10px 16px', borderRadius: '10px',
                border: '1.5px solid ' + (selected ? '#8B1A4A' : '#E8E8E8'),
                background: selected ? '#8B1A4A' : '#fff', color: selected ? '#fff' : '#8B1A4A',
                fontSize: '14px', fontWeight: '600', cursor: 'pointer'
              },
              onClick: function () { updateData('periodDays', stored); }
            }, isPlus ? '7+' : (d === 1 ? '1 day' : (d + ' days')));
          })
        ),

        // Cycle length
        h('label', { style: { fontSize: '13px', fontWeight: '600', color: '#8B1A4A', display: 'block', marginBottom: '8px' } }, 'Typical cycle length (days between periods)'),
        h('div', { style: { display: 'flex', flexWrap: 'wrap', gap: '6px' } },
          ...cycleLengthOptions.map(function (opt) {
            var selected = Number(data.cycleLength) === opt.value;
            return h('button', {
              type: 'button',
              style: {
                padding: '8px 14px', borderRadius: '10px',
                border: '1.5px solid ' + (selected ? '#8B1A4A' : '#E8E8E8'),
                background: selected ? '#8B1A4A' : '#fff', color: selected ? '#fff' : '#8B1A4A',
                fontSize: '13px', fontWeight: '600', cursor: 'pointer'
              },
              onClick: function () { updateData('cycleLength', opt.value); }
            }, opt.label);
          })
        )
      );
    }

    // ── Step 2: Concerns, Pain, Supplements ──
    if (step === 2) {
      var concerns = ['Cramps', 'Bloating', 'Fatigue', 'Mood swings', 'Irregular periods', 'Sleep issues', 'Skin/acne', 'PCOS'];
      var painLevels = [
        { val: 0, label: 'No pain' },
        { val: 1, label: 'Mild' },
        { val: 2, label: 'Moderate' },
        { val: 3, label: 'Bad' },
        { val: 4, label: 'Severe' },
      ];
      var supplements = ['Iron', 'B12', 'Magnesium', 'Vitamin D', 'Multivitamin', 'None'];

      content = h('div', { style: { padding: '24px 22px' } },
        h('h2', { style: { fontSize: '20px', fontWeight: '800', color: '#8B1A4A', margin: '0 0 4px' } }, 'Almost there'),
        h('p', { style: { fontSize: '13px', color: '#666', margin: '0 0 20px' } }, 'This shapes your Health Score.'),

        // Main concerns (multi-select)
        h('label', { style: { fontSize: '13px', fontWeight: '600', color: '#8B1A4A', display: 'block', marginBottom: '8px' } }, 'Main concerns (pick all that apply)'),
        h('div', { style: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '24px' } },
          ...concerns.map(function (c) {
            var sel = (data.concerns || []).indexOf(c) !== -1;
            return h('button', {
              style: {
                padding: '10px 16px', borderRadius: '10px',
                border: '1.5px solid ' + (sel ? '#8B1A4A' : '#E8E8E8'),
                background: sel ? '#8B1A4A' : '#fff', color: sel ? '#fff' : '#8B1A4A',
                fontSize: '13px', fontWeight: '600', cursor: 'pointer'
              },
              onClick: function () { toggleMulti('concerns', c); }
            }, c);
          })
        ),

        // Pain level
        h('label', { style: { fontSize: '13px', fontWeight: '600', color: '#8B1A4A', display: 'block', marginBottom: '8px' } }, 'Period pain level'),
        h('div', { style: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '24px' } },
          ...painLevels.map(function (pl) {
            var sel = data.painLevel === pl.val;
            return h('button', {
              type: 'button',
              style: {
                flex: '1 1 18%', minWidth: '64px', padding: '12px 6px', borderRadius: '10px', textAlign: 'center',
                border: '1.5px solid ' + (sel ? '#8B1A4A' : '#E8E8E8'),
                background: sel ? '#8B1A4A' : '#fff', color: sel ? '#fff' : '#8B1A4A',
                fontSize: '13px', fontWeight: '600', cursor: 'pointer'
              },
              onClick: function () { updateData('painLevel', pl.val); }
            },
              h('div', { style: { fontWeight: '700' } }, String(pl.val)),
              h('div', { style: { fontSize: '11px', marginTop: '2px', opacity: '0.7' } }, pl.label)
            );
          })
        ),

        // Supplements (multi-select)
        h('label', { style: { fontSize: '13px', fontWeight: '600', color: '#8B1A4A', display: 'block', marginBottom: '8px' } }, 'Currently taking supplements?'),
        h('div', { style: { display: 'flex', flexWrap: 'wrap', gap: '8px' } },
          ...supplements.map(function (s) {
            var sel = (data.supplements || []).indexOf(s) !== -1;
            return h('button', {
              style: {
                padding: '10px 16px', borderRadius: '10px',
                border: '1.5px solid ' + (sel ? '#8B1A4A' : '#E8E8E8'),
                background: sel ? '#8B1A4A' : '#fff', color: sel ? '#fff' : '#8B1A4A',
                fontSize: '13px', fontWeight: '600', cursor: 'pointer'
              },
              onClick: function () { toggleSupplements(s); }
            }, s);
          })
        )
      );
    }

    // Validate step for continue button
    function canContinue() {
      if (step === 0) return data.name && data.ageGroup && data.consent_health && data.consent_age && data.consent_private;
      if (step === 1) return data.lastPeriod && data.periodDays && data.cycleLength;
      // API requires supplements.min(1) — must block Continue until at least one is selected.
      if (step === 2) {
        return (data.concerns && data.concerns.length > 0)
          && data.painLevel != null
          && (data.supplements && data.supplements.length > 0);
      }
      return false;
    }

    // Navigation buttons — sticky so Theme Editor / browser chrome can't cover the CTA
    var saving = !!state.onboardSaving;
    var ready = canContinue() && !saving;
    var nav = h('div', {
      className: 'hf-onboard-nav',
      style: {
        padding: '12px 22px calc(20px + env(safe-area-inset-bottom, 0px))',
        display: 'flex', gap: '10px',
        position: 'sticky', bottom: '0', background: '#fff',
        borderTop: '1px solid #F0E0E8', zIndex: '20',
        boxShadow: '0 -8px 24px rgba(139,26,74,0.06)'
      }
    },
      step > 0 ? h('button', {
        type: 'button',
        style: {
          padding: '14px 24px', borderRadius: '12px', border: '1.5px solid #E8E8E8',
          background: '#fff', color: '#8B1A4A', fontSize: '15px', fontWeight: '600', cursor: 'pointer'
        },
        onClick: function () { if (!saving) setState({ onboardStep: step - 1 }); }
      }, 'Back') : null,
      h('button', {
        type: 'button',
        className: 'hf-btn hf-btn--cta',
        disabled: !ready ? 'disabled' : null,
        style: {
          flex: '1', padding: '14px 12px', borderRadius: '12px', border: 'none',
          background: ready ? 'linear-gradient(135deg, #E91E8C, #C2185B)' : '#E8E8E8',
          color: ready ? '#fff' : '#999',
          fontSize: '15px', fontWeight: '700',
          cursor: ready ? 'pointer' : 'default',
          opacity: saving ? '0.85' : '1'
        },
        onClick: function (ev) {
          if (ev) { ev.preventDefault(); ev.stopPropagation(); }
          if (!canContinue() || saving) return;
          if (step === totalSteps - 1 && state.onboardSaving) return;
          evTrack('Onboarding_StepCompleted', { index: step });
          trackOnboardAnswers(step);
          if (step < totalSteps - 1) {
            setState({ onboardStep: step + 1 });
          } else {
            // Final step — save profile
            // Name is written via onInput without setState — re-read DOM so submit isn't empty.
            var nameEl = document.getElementById('hf-onboard-name');
            if (nameEl && nameEl.value) {
              data.name = nameEl.value.trim();
              state.onboardData.name = data.name;
            }
            fabscoreTrack('onboard_complete', data);
            // Calculate initial score from M0 answers
            var painScores = { 0: 100, 1: 90, 2: 70, 3: 45, 4: 20 };
            var concernPenalty = Math.min((data.concerns || []).length * 5, 25);
            var suppBonus = (data.supplements || []).indexOf('None') === -1 && (data.supplements || []).length > 0 ? 10 : 0;
            var m0Score = Math.max(10, Math.min(100, (painScores[data.painLevel] || 60) - concernPenalty + suppBonus));

            var fbCust = (window.__fabscore && window.__fabscore.customer) || {};
            var profilePayload = {
              name: data.name,
              email: fbCust.email || undefined,
              age: data.ageGroup,
              lastPeriod: data.lastPeriod,
              periodDays: data.periodDays,
              cycleLength: data.cycleLength,
              mainConcern: data.concerns,
              painLevel: data.painLevel,
              // Backend requires ≥1 supplement; "None" is a valid choice.
              supplements: (data.supplements && data.supplements.length) ? data.supplements : ['None'],
              initialScore: m0Score,
              consent: {
                collect: !!data.consent_health,
                store: !!data.consent_age,
                rights: !!data.consent_private,
              },
              entryProduct: state.urlParams.product || null,
              entryRef: state.urlParams.ref || null,
            };
            if (DEV_SKIP_API) {
              setState({ profile: profilePayload, screen: 'score', onboardSaving: false });
              return;
            }
            setState({ onboardSaving: true });
            fabscoreAPI.saveProfile(profilePayload).then(function (res) {
              if (res.ok) {
                evTrack('Onboarding_Completed', {}, true);
                setState({ profile: res.data || profilePayload, screen: 'score', onboardSaving: false });
                return;
              }
              // Theme Editor: no reliable OTP + prod API may lag local validators
              // (e.g. periodDays 1–2 while live still has min 3). Preview score anyway.
              if (window.Shopify && window.Shopify.designMode) {
                setState({ profile: profilePayload, screen: 'score', onboardSaving: false });
                return;
              }
              state.onboardSaving = false;
              if (!handleAPIError(res)) {
                showError(errTypeOf(res), apiErrorMessage(res, 'Could not save your details. Please try again.'), function () {
                  setState({ onboardStep: step, onboardSaving: false });
                });
              } else {
                setState({ onboardSaving: false });
              }
            });
          }
        }
      }, saving ? 'Saving…' : (step === totalSteps - 1 ? 'Check my score' : 'Continue'))
    );

    return h('div', { className: 'hf-onboarding', style: { minHeight: '100vh', background: '#fff', display: 'flex', flexDirection: 'column' } },
      progress, h('div', { style: { flex: '1' } }, content), nav
    );
  }

  // ─── SCREEN: CHECKIN ────────────────────────────────────────
  var TP_NAMES = { 1: 'Period start check', 2: 'Day 2 energy', 14: 'Mid-cycle nutrition', 22: 'Luteal bloating' };
  var capitalize = function (s) { s = String(s || ''); return s.charAt(0).toUpperCase() + s.slice(1); };
  var SL = function (s) {
    if (s >= 85) return 'Optimal';
    if (s >= 70) return 'Functional';
    if (s >= 50) return 'Sub-optimal';
    if (s >= 30) return 'Deficient';
    return 'Clinical review';
  };

  // Reusable WhatsApp reminder opt-in button. On success it flips the label to
  // "Reminder on" with a check and shows a confirmation toast.
  function renderReminderButton(opts) {
    opts = opts || {};
    var ghost = !!opts.ghost;
    var btn = h('button', {
      className: 'hf-btn',
      style: ghost ? {
        width: '100%', padding: '12px 0', borderRadius: '14px',
        border: '1.5px solid #F4C0D1', background: '#fff', color: '#D4537E',
        fontSize: '14px', fontWeight: '700', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
      } : {
        width: '100%', padding: '14px 0', borderRadius: '14px', border: 'none',
        background: '#D4537E', color: '#fff', fontSize: '15px', fontWeight: '700', cursor: 'pointer',
        boxShadow: '0 4px 20px rgba(212,83,126,0.25)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
      }
    }, 'Remind me on WhatsApp');

    btn.addEventListener('click', function () {
      if (btn.dataset.on === '1') return;
      btn.disabled = true;
      Promise.resolve(fabscoreAPI.setReminderPreference(true)).then(function (res) {
        btn.disabled = false;
        if (res && res.ok) {
          evTrack('CheckinReminder_OptIn', {});
          showToast("We'll WhatsApp you before your next check-in");
          btn.dataset.on = '1';
          btn.textContent = '';
          btn.appendChild(makeIcon('check', ghost ? '#D4537E' : '#fff', 16));
          btn.appendChild(h('span', null, 'Reminder on'));
        } else {
          showToast("Couldn't set your reminder. Please try again.");
        }
      });
    });
    return btn;
  }

  // Dated next-check-in hero card, shared by the "no check-in today" and
  // "done for today" states. Pink-tinted, with the real date and a question row.
  function renderNextCheckinCard(nextTP) {
    return h('div', {
      style: { background: '#FFF0F6', border: '1px solid #F4C0D1', borderRadius: '16px', padding: '18px', margin: '0 16px 14px' }
    },
      h('p', { style: { fontSize: '11px', fontWeight: '700', color: '#993556', textTransform: 'uppercase', letterSpacing: '.07em', margin: '0 0 6px' } }, 'Your next check-in'),
      h('p', { style: { fontSize: '26px', fontWeight: '800', color: '#8B1A4A', margin: '0', lineHeight: '1.1', letterSpacing: '-0.3px' } }, formatNextDate(nextTP.daysAway)),
      h('p', { style: { fontSize: '13px', color: '#993556', margin: '4px 0 14px' } }, nextTP.daysAway === 1 ? 'tomorrow' : 'in ' + nextTP.daysAway + ' days'),
      h('div', {
        style: { background: '#fff', borderRadius: '12px', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: '12px' }
      },
        h('div', {
          style: { width: '34px', height: '34px', borderRadius: '9px', background: '#FFF0F6', border: '1px solid #F4C0D1', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: '0' }
        }, makeIcon('check', '#D4537E', 16)),
        h('div', null,
          h('p', { style: { fontSize: '14px', fontWeight: '700', color: '#8B1A4A', margin: '0' } }, nextTouchpointLabel(nextTP.day)),
          h('p', { style: { fontSize: '12px', color: '#993556', opacity: '0.75', margin: '2px 0 0' } }, '1 question · about 10 seconds')
        )
      )
    );
  }

  function renderCheckin() {
    var profile = state.profile || {};
    var cycleDay = getDayOfCycle(profile.lastPeriod, profile.cycleLength);
    var phase = getPhase(cycleDay);
    var tp = getTouchpoint(cycleDay, state.history || [], profile);
    var nextTP = getNextTouchpoint(cycleDay);

    var wrapper = h('div', { className: 'hf-checkin', style: { background: 'radial-gradient(130% 70% at 50% 0%, #FFF4F8 0%, #FFFFFF 55%)', minHeight: '100vh', paddingBottom: 'calc(84px + env(safe-area-inset-bottom, 0px))' } });
    wrapper.appendChild(renderLogoutBar());

    // Earn toast
    var toast = renderEarnToast();
    if (toast) wrapper.appendChild(toast);

    // ── Already logged today ──
    if (state.todayLog) {
      var logScore = state.todayLog.score || 50;
      var logGrade = getGrade(logScore);
      var logIconName = logGrade.grade === 'A' ? 'trophy' : logGrade.grade === 'B' ? 'trending' : logGrade.grade === 'C' ? 'target' : 'alert';

      wrapper.appendChild(h('div', { style: { padding: '20px 16px 16px' } },
        h('p', { style: { fontSize: '12px', color: '#bbb', margin: '0 0 3px', letterSpacing: '0.02em' } }, 'Cycle day ' + cycleDay),
        h('h1', { style: { fontSize: '20px', fontWeight: '800', color: '#D4537E', margin: '0' } }, 'done for today')
      ));
      var scoreCard = h('div', {
        style: { background: phase.bg, borderRadius: '22px', padding: '24px', margin: '0 16px 14px', textAlign: 'center' }
      });
      var logIconWrap = h('div', { style: { marginBottom: '8px', display: 'flex', justifyContent: 'center' } });
      logIconWrap.appendChild(makeIcon(logIconName, phase.color, 40));
      scoreCard.appendChild(logIconWrap);
      scoreCard.appendChild(h('p', { style: { fontSize: '42px', fontWeight: '900', color: phase.color, margin: '0 0 4px', lineHeight: '1', letterSpacing: '-1px' } }, String(logScore)));
      scoreCard.appendChild(h('p', { style: { fontSize: '14px', fontWeight: '700', color: phase.color, margin: '0 0 4px' } }, SL(logScore)));
      scoreCard.appendChild(h('p', { style: { fontSize: '12px', color: phase.color, opacity: '0.7', margin: '0' } }, phase.label + ' phase'));
      wrapper.appendChild(scoreCard);
      // Dated next check-in card (same style as the "no check-in today" state)
      wrapper.appendChild(renderNextCheckinCard(nextTP));
      wrapper.appendChild(h('div', { style: { padding: '0 16px 8px' } },
        renderReminderButton({ ghost: true })
      ));
      wrapper.appendChild(h('div', { style: { padding: '0 16px 16px' } },
        h('button', {
          className: 'hf-btn hf-btn--cta',
          style: { width: '100%', padding: '14px 0', borderRadius: '14px', border: 'none', background: '#D4537E', color: '#fff', fontSize: '15px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 20px rgba(212,83,126,0.25)' },
          onClick: function () { setState({ screen: 'score' }); }
        }, 'See my Health Score')
      ));
      wrapper.appendChild(renderBottomNav('checkin'));
      return wrapper;
    }

    // ── No touchpoint today ──
    if (!tp) {
      // Small affirming status line (demoted from the old big headline)
      wrapper.appendChild(h('div', { style: { padding: '20px 16px 12px', display: 'flex', alignItems: 'center', gap: '6px' } },
        makeIcon('check', '#30A46C', 16),
        h('p', { style: { fontSize: '13px', fontWeight: '700', color: '#30A46C', margin: '0' } }, "You're all caught up")
      ));

      // HERO: dated next check-in card
      wrapper.appendChild(renderNextCheckinCard(nextTP));

      // PRIMARY CTA: WhatsApp reminder
      wrapper.appendChild(h('div', { style: { padding: '0 16px 10px' } },
        renderReminderButton({ ghost: false })
      ));

      // SECONDARY CTA: see today's Health Score
      wrapper.appendChild(h('div', { style: { padding: '0 16px 16px' } },
        h('button', {
          className: 'hf-btn',
          style: { width: '100%', padding: '13px 0', borderRadius: '14px', border: '1.5px solid #F4C0D1', background: '#fff', color: '#D4537E', fontSize: '15px', fontWeight: '700', cursor: 'pointer' },
          onClick: function () { setState({ screen: 'score' }); }
        }, "See today's Health Score")
      ));

      // COLLAPSIBLE: how check-ins work (collapsed by default)
      var helpOpen = !!state.showCheckinHelp;
      wrapper.appendChild(h('div', { style: { margin: '0 16px 16px' } },
        h('button', {
          className: 'hf-btn',
          style: { width: '100%', padding: '12px 4px', background: 'none', border: 'none', borderTop: '1px solid #f0e3e9', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
          onClick: function () { setState({ showCheckinHelp: !state.showCheckinHelp }); }
        },
          h('span', { style: { fontSize: '14px', fontWeight: '700', color: '#8B1A4A' } }, 'How check-ins work'),
          h('span', { style: { fontSize: '14px', color: '#993556', transform: helpOpen ? 'rotate(90deg)' : 'none', transition: 'transform .15s ease' } }, '›')
        ),
        helpOpen ? h('div', { style: { paddingTop: '6px' } },
          h('p', { style: { fontSize: '13px', color: '#888', lineHeight: '1.6', margin: '0 0 12px' } }, 'healthfab checks in 5 times per cycle — one question at a time, always when it matters most.'),
          h('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' } },
            ...[
              { d: '1', l: 'Period start', c: '#E5484D', bg: '#FFF0F0' },
              { d: '2', l: 'Day 2 energy', c: '#E79D13', bg: '#FFF8E1' },
              { d: '14', l: 'Nutrition check', c: '#30A46C', bg: '#E8FAF0' },
              { d: '22-28', l: 'Luteal + sleep', c: '#6E56CF', bg: '#F0EFFE' },
            ].map(function (p) {
              return h('div', {
                style: { background: p.bg, borderRadius: '12px', padding: '10px 12px', border: '1px solid ' + p.c + '25' }
              },
                h('p', { style: { fontSize: '10px', fontWeight: '700', color: p.c, margin: '0 0 2px' } }, 'Day ' + p.d),
                h('p', { style: { fontSize: '12px', color: p.c, margin: '0' } }, p.l)
              );
            })
          )
        ) : null
      ));

      wrapper.appendChild(renderBottomNav('checkin'));
      return wrapper;
    }

    // ── Show insight after answer ──
    if (state.showInsight && state.selectedOpt) {
      var insightItems = tp.insights(state.checkinAnswers);
      var selOpt = state.selectedOpt;

      wrapper.appendChild(h('div', { style: { padding: '20px 16px 14px' } },
        h('p', { style: { fontSize: '12px', color: '#D4537E', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '.06em', margin: '0 0 4px' } }, "Today's check-in · " + capitalize(phase.label)),
        h('h1', { style: { fontSize: '22px', fontWeight: '800', color: '#D4537E', margin: '0', lineHeight: '1.2' } }, 'here\'s what that means for you')
      ));

      // Answer recap
      wrapper.appendChild(h('div', {
        style: { background: '#FFF0F6', borderRadius: '16px', padding: '14px 16px', margin: '0 16px 16px', display: 'flex', alignItems: 'center', gap: '12px' }
      },
        h('div', {
          style: { width: '40px', height: '40px', borderRadius: '10px', background: '#D4537E', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: '0' }
        }, makeIcon('check', '#fff', 18)),
        h('div', null,
          h('p', { style: { fontSize: '11px', color: '#D4537E', fontWeight: '600', margin: '0 0 2px', textTransform: 'uppercase', letterSpacing: '.04em' } }, 'you said'),
          h('p', { style: { fontSize: '14px', fontWeight: '700', color: '#D4537E', margin: '0' } }, selOpt.l),
          h('p', { style: { fontSize: '11px', color: '#D4537E', opacity: '0.7', margin: '2px 0 0' } }, selOpt.vibe)
        )
      ));

      // Numbered insight cards
      wrapper.appendChild(h('div', { style: { display: 'flex', flexDirection: 'column', gap: '10px', margin: '0 16px 20px' } },
        ...insightItems.map(function (item, i) {
          return h('div', {
            style: { background: '#fff', borderRadius: '16px', padding: '14px 16px', boxShadow: '0 1px 4px rgba(212,83,126,0.06)', display: 'flex', gap: '12px', alignItems: 'flex-start' }
          },
            h('div', {
              style: { width: '28px', height: '28px', borderRadius: '8px', background: '#FFF0F6', border: '1.5px solid #F4C0D1', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: '0' }
            }, h('span', { style: { fontSize: '12px', fontWeight: '800', color: '#D4537E' } }, String(i + 1))),
            h('p', { style: { fontSize: '14px', color: '#222', lineHeight: '1.6', margin: '0' } }, item)
          );
        })
      ));

      // Score preview
      wrapper.appendChild(h('div', {
        style: { background: '#FFF0F6', borderRadius: '16px', padding: '14px 16px', margin: '0 16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }
      },
        h('div', null,
          h('p', { style: { fontSize: '12px', color: '#D4537E', fontWeight: '600', margin: '0 0 2px' } }, 'today\'s Health Score'),
          h('p', { style: { fontSize: '11px', color: '#D4537E', opacity: '0.7', margin: '0' } }, tp.formula)
        ),
        h('div', { style: { textAlign: 'right' } },
          h('p', { style: { fontSize: '32px', fontWeight: '900', color: '#D4537E', margin: '0', lineHeight: '1' } }, String(selOpt.s)),
          h('p', { style: { fontSize: '11px', color: '#D4537E', margin: '2px 0 0' } }, SL(selOpt.s))
        )
      ));

      // Save button
      wrapper.appendChild(h('div', { style: { padding: '0 16px' } },
        h('button', {
          className: 'hf-btn hf-btn--cta',
          style: {
            width: '100%', padding: '16px 0', borderRadius: '16px', border: 'none',
            background: '#D4537E', color: '#fff', fontSize: '16px', fontWeight: '800', cursor: 'pointer', letterSpacing: '-0.2px',
            boxShadow: '0 4px 20px rgba(212,83,126,0.25)'
          },
          onClick: function () {
            var score = calcScore(state.checkinAnswers, phase, state.history);
            var logData = {
              date: TODAY,
              touchpointId: tp.id,
              cycleDay: cycleDay,
              phase: phase.key || phase.label.toLowerCase(),
              answers: state.checkinAnswers,
              score: score,
            };
            if (DEV_SKIP_API) {
              setState({ todayLog: logData, earnResult: { earned: 25 }, screen: 'score', showInsight: false, selectedOpt: null });
              return;
            }
            fabscoreAPI.saveLog(logData).then(function (res) {
              if (res.ok) {
                evTrack('Checkin_Completed', { score: score, touchpointId: tp.id }, true);
                // N1 confirming a new period resets the cycle to day 1 — M1
                // (the real day-1 check) then queues in on the next visit,
                // instead of N1 asking "has it started?" every day forever.
                if (tp.id === 'N1' && selOpt.l === 'Yes — started today') {
                  if (state.profile) state.profile.lastPeriod = TODAY;
                  fabscoreAPI.setPeriodDate(TODAY);
                }
                // Points are awarded server-side; read the earned amount back.
                var pts = (res.data && res.data.rewardPoints) || 0;
                setState({
                  todayLog: (res.data && res.data.log) || logData,
                  earnResult: pts > 0 ? { earned: pts } : null,
                  screen: 'score',
                  showInsight: false,
                  selectedOpt: null,
                });
              } else if (res.code === 'ALREADY_LOGGED') {
                // Already checked in today — show the existing log on the score screen.
                var existing = (res.details && res.details.existingLog) || logData;
                setState({ todayLog: existing, screen: 'score', showInsight: false, selectedOpt: null });
              } else if (!handleAPIError(res)) {
                showError(errTypeOf(res), res.message || 'Could not save your check-in. Please try again.', function () {
                  setState({ showInsight: true });
                });
              }
            });
          }
        }, 'Save to my Health Score')
      ));
      wrapper.appendChild(renderBottomNav('checkin'));
      return wrapper;
    }

    // ── Touchpoint question ──
    var q = tp.questions[0];
    wrapper.appendChild(h('div', { style: { padding: '20px 16px 14px' } },
      h('p', { style: { fontSize: '12px', color: tp.color, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '.06em', margin: '0 0 4px' } }, "Today's check-in · " + capitalize(tp.phase_label)),
      h('h1', { style: { fontSize: '22px', fontWeight: '800', color: '#111', margin: '0 0 4px', lineHeight: '1.2' } }, tp.heading),
      h('p', { style: { fontSize: '13px', color: '#888', margin: '0 0 2px' } }, tp.subhead),
      h('p', { style: { fontSize: '12px', color: '#bbb', margin: '0' } }, '1 question · about 10 seconds')
    ));

    // Question card with radio-style options
    wrapper.appendChild(h('div', {
      style: { background: tp.bg, borderRadius: '24px', padding: '22px', margin: '0 16px 20px', border: '1.5px solid ' + tp.border, boxShadow: '0 16px 40px -18px ' + tp.color + '55' }
    },
      h('p', { style: { fontSize: '18px', fontWeight: '700', color: tp.color, lineHeight: '1.35', margin: '0 0 18px' } }, q.ask),
      h('div', { style: { display: 'flex', flexDirection: 'column', gap: '10px' } },
        ...q.options.map(function (opt) {
          var isSelected = state.selectedOpt && state.selectedOpt.l === opt.l;
          return h('button', {
            style: {
              padding: '16px 18px', borderRadius: '14px',
              border: '2px solid ' + (isSelected ? tp.color : 'rgba(0,0,0,0.06)'),
              background: isSelected ? tp.color : '#fff',
              color: isSelected ? '#fff' : '#222',
              fontSize: '15px', cursor: 'pointer', textAlign: 'left', fontWeight: '500',
              display: 'flex', alignItems: 'center', gap: '12px',
              transition: 'transform .14s ease, box-shadow .14s ease, border-color .14s ease',
              boxShadow: isSelected ? '0 10px 22px -8px ' + tp.color + '88' : '0 1px 3px rgba(0,0,0,0.05)',
              transform: isSelected ? 'translateY(-1px)' : 'none'
            },
            onClick: function () {
              var ans = {};
              ans[q.id] = { l: opt.l, s: opt.s, vibe: opt.vibe };
              state.selectedOpt = opt;
              state.checkinAnswers = Object.assign({}, state.checkinAnswers, ans);
              if (!_ckStarted[tp.id]) { _ckStarted[tp.id] = true; evTrack('Checkin_Started', { touchpointId: tp.id }); }
              evTrack('Checkin_QuestionAnswered', { touchpointId: tp.id, questionId: q.id, optionLabel: opt.l, score: opt.s });
              // Auto-advance to insight after brief delay
              setTimeout(function () { setState({ showInsight: true }); }, 300);
            }
          },
            // Radio circle
            h('div', {
              style: {
                width: '22px', height: '22px', borderRadius: '50%',
                border: '2px solid ' + (isSelected ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.15)'),
                background: 'transparent', flexShrink: '0',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }
            }, isSelected ? h('div', { style: { width: '10px', height: '10px', borderRadius: '50%', background: '#fff' } }) : null),
            opt.l
          );
        })
      )
    ));

    wrapper.appendChild(h('p', { style: { fontSize: '11px', color: '#bbb', textAlign: 'center', margin: '0' } }, 'answer to get your personalised insight · 1 question · about 10 seconds'));
    wrapper.appendChild(renderBottomNav('checkin'));
    return wrapper;
  }

  // ─── SCREEN: PERIOD CALCULATOR ──────────────────────────────
  // Fetches cycle predictions once per open (guarded by _predFetched). The
  // backend returns either { available:false } or the full prediction model.
  var _predFetched = false;
  var _calcTracked = false;
  var PHASE_TIPS = {
    menstrual: 'Rest, iron-rich food, gentle movement',
    follicular: 'New projects, hard workouts, social plans',
    ovulatory: 'Bold decisions, presentations, peak effort',
    luteal: 'Winding down, reflection, self-care',
  };

  function loadPredictions() {
    if (_predFetched || DEV_SKIP_API) return;
    _predFetched = true;
    setState({ predLoading: true });
    Promise.resolve(fabscoreAPI.getPredictions()).then(function (res) {
      if (res && res.ok) {
        setState({ predictions: res.data || { available: false }, predLoading: false });
      } else {
        setState({ predLoading: false });
      }
    }).catch(function () { setState({ predLoading: false }); });
  }

  function renderCalculator() {
    // One-time screen-view track per open.
    if (!_calcTracked) { _calcTracked = true; evTrack('Screen_Viewed', { screen: 'calculator' }); }

    var data = state.predictions;
    // Kick off the one-time fetch if we have nothing yet.
    if (data == null) loadPredictions();

    var profile = state.profile || {};
    var cycleLength = (data && data.cycleLength) || profile.cycleLength || 28;

    var wrapper = h('div', {
      className: 'hf-calculator',
      style: { background: 'radial-gradient(130% 70% at 50% 0%, #FFF4F8 0%, #FFFFFF 55%)', minHeight: '100vh', paddingBottom: 'calc(84px + env(safe-area-inset-bottom, 0px))' }
    });
    wrapper.appendChild(renderLogoutBar());

    // ── Header ──
    wrapper.appendChild(h('div', { style: { padding: '20px 16px 12px' } },
      h('h1', { style: { fontSize: '24px', fontWeight: '800', color: '#8B1A4A', margin: '0 0 3px', letterSpacing: '-0.3px' } }, 'Period calculator'),
      h('p', { style: { fontSize: '13px', color: '#993556', opacity: '0.7', margin: '0' } }, 'Based on your ' + cycleLength + '-day cycle')
    ));

    // ── Loading / not-yet-fetched ──
    if (data == null) {
      wrapper.appendChild(h('div', { style: { padding: '40px 16px', textAlign: 'center' } },
        h('p', { style: { fontSize: '14px', color: '#993556', opacity: '0.7', margin: '0' } }, 'Loading your cycle calendar…')
      ));
      wrapper.appendChild(renderBottomNav('calculator'));
      return wrapper;
    }

    // ── Empty state ──
    if (data.available === false) {
      wrapper.appendChild(h('div', {
        style: { background: '#FFF0F6', border: '1px solid #F4C0D1', borderRadius: '20px', padding: '28px 22px', margin: '8px 16px 16px', textAlign: 'center' }
      },
        h('div', { style: { display: 'flex', justifyContent: 'center', marginBottom: '12px' } }, makeIcon('calendar', '#D4537E', 36)),
        h('h2', { style: { fontSize: '17px', fontWeight: '800', color: '#8B1A4A', margin: '0 0 6px' } }, 'Your calendar is almost ready'),
        h('p', { style: { fontSize: '13px', color: '#993556', opacity: '0.8', lineHeight: '1.5', margin: '0 0 18px' } }, 'Complete your setup to see your cycle calendar'),
        h('button', {
          className: 'hf-btn hf-btn--cta',
          style: { width: '100%', padding: '14px 0', borderRadius: '14px', border: 'none', background: '#D4537E', color: '#fff', fontSize: '15px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 20px rgba(212,83,126,0.25)' },
          onClick: function () { setState({ screen: 'checkin' }); }
        }, 'Complete my setup')
      ));
      wrapper.appendChild(renderBottomNav('calculator'));
      return wrapper;
    }

    var curPhase = PHASES[data.phase] || PHASES.menstrual;

    // ── Current-status card (tinted by current phase) ──
    var statusSub;
    if (data.isOnPeriod) {
      statusSub = 'Period day ' + data.cycleDay + ' — rest and nourish';
    } else if (data.daysUntilNext > 0) {
      statusSub = 'Next period in ' + data.daysUntilNext + ' days (' + fmtDate(data.nextPeriod && data.nextPeriod.start) + ')';
    } else {
      statusSub = 'Period expected very soon';
    }
    var statusCard = h('div', {
      style: { background: curPhase.bg, border: '1px solid ' + curPhase.border, borderRadius: '20px', padding: '20px', margin: '4px 16px 14px', boxShadow: '0 16px 40px -22px ' + curPhase.color + '55' }
    },
      h('div', { style: { display: 'flex', alignItems: 'center', gap: '14px' } },
        h('div', {
          style: { width: '48px', height: '48px', borderRadius: '14px', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: '0', boxShadow: '0 4px 12px -4px ' + curPhase.color + '55' }
        }, makeIcon(curPhase.iconKey, curPhase.color, 24)),
        h('div', { style: { minWidth: '0' } },
          h('p', { style: { fontSize: '17px', fontWeight: '800', color: curPhase.color, margin: '0 0 2px', lineHeight: '1.2' } }, curPhase.label + ' phase'),
          h('p', { style: { fontSize: '12px', color: curPhase.color, opacity: '0.75', margin: '0' } }, 'Day ' + data.cycleDay + ' of your cycle')
        )
      ),
      h('p', { style: { fontSize: '13px', fontWeight: '600', color: curPhase.color, margin: '14px 0 0', lineHeight: '1.45' } }, statusSub)
    );
    wrapper.appendChild(statusCard);

    // ── Cycle phase bar ──
    var phases = data.phases || [];
    if (phases.length) {
      var bar = h('div', { style: { display: 'flex', gap: '3px', borderRadius: '10px', overflow: 'hidden', height: '44px' } });
      phases.forEach(function (seg) {
        var ph = PHASES[seg.phase] || PHASES.menstrual;
        bar.appendChild(h('div', {
          style: {
            flex: String(seg.days || 1), background: ph.bg,
            borderBottom: seg.active ? '3px solid ' + ph.color : '3px solid transparent',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '10px', fontWeight: seg.active ? '800' : '600',
            color: ph.color, opacity: seg.active ? '1' : '0.7', overflow: 'hidden', whiteSpace: 'nowrap'
          },
          title: seg.label || ph.label
        }, makeIcon(ph.iconKey, ph.color, 14)));
      });
      wrapper.appendChild(h('div', {
        style: { background: '#fff', border: '1px solid #F1DCE6', borderRadius: '18px', padding: '16px', margin: '0 16px 14px', boxShadow: '0 8px 20px -14px rgba(212,83,126,0.3)' }
      },
        h('p', { style: { fontSize: '13px', fontWeight: '800', color: '#8B1A4A', margin: '0 0 12px' } }, 'Your cycle this month'),
        bar,
        h('p', { style: { fontSize: '11px', color: '#999', textAlign: 'center', margin: '10px 0 0' } }, 'Cycle day ' + data.cycleDay + ' of ' + cycleLength)
      ));
    }

    // ── Upcoming periods ──
    var preds = data.predictions || [];
    if (preds.length) {
      var listCard = h('div', {
        style: { background: '#fff', border: '1px solid #F1DCE6', borderRadius: '18px', padding: '16px', margin: '0 16px 14px', boxShadow: '0 8px 20px -14px rgba(212,83,126,0.3)' }
      },
        h('p', { style: { fontSize: '13px', fontWeight: '800', color: '#8B1A4A', margin: '0 0 12px' } }, 'Upcoming periods')
      );
      preds.forEach(function (p, i) {
        var dotColor = p.status === 'current' ? '#D4537E' : p.status === 'past' ? '#CFCFCF' : '#F4C0D1';
        var rightLabel = p.status === 'current' ? 'Now'
          : p.status === 'past' ? 'Done'
          : (p.daysAway === 0 ? 'Today' : 'In ' + p.daysAway + 'd');
        var isPast = p.status === 'past';
        listCard.appendChild(h('div', {
          style: { display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 0', borderTop: i === 0 ? 'none' : '1px solid #F7F2F5', opacity: isPast ? '0.6' : '1' }
        },
          h('div', { style: { width: '10px', height: '10px', borderRadius: '50%', background: dotColor, flexShrink: '0' } }),
          h('div', { style: { flex: '1', minWidth: '0' } },
            h('p', { style: { fontSize: '14px', fontWeight: '700', color: '#222', margin: '0' } }, fmtDate(p.start) + ' – ' + fmtDate(p.end)),
            h('p', { style: { fontSize: '11px', color: '#999', margin: '2px 0 0' } }, (data.periodDays || 5) + ' days expected')
          ),
          h('span', {
            style: {
              fontSize: '11px', fontWeight: '800',
              color: p.status === 'current' ? '#D4537E' : '#993556',
              opacity: p.status === 'current' ? '1' : '0.7',
              background: p.status === 'current' ? '#FFF0F6' : 'transparent',
              padding: p.status === 'current' ? '3px 8px' : '0', borderRadius: '999px', whiteSpace: 'nowrap'
            }
          }, rightLabel)
        ));
      });
      listCard.appendChild(h('p', { style: { fontSize: '11px', color: '#bbb', textAlign: 'center', margin: '12px 0 0' } }, 'Predictions improve as you log more data'));
      wrapper.appendChild(listCard);
    }

    // ── Phase guide ──
    var guideCard = h('div', {
      style: { background: '#fff', border: '1px solid #F1DCE6', borderRadius: '18px', padding: '16px', margin: '0 16px 14px', boxShadow: '0 8px 20px -14px rgba(212,83,126,0.3)' }
    },
      h('p', { style: { fontSize: '13px', fontWeight: '800', color: '#8B1A4A', margin: '0 0 12px' } }, 'Phase guide')
    );
    ['menstrual', 'follicular', 'ovulatory', 'luteal'].forEach(function (key, i) {
      var ph = PHASES[key];
      guideCard.appendChild(h('div', {
        style: { display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '10px 0', borderTop: i === 0 ? 'none' : '1px solid #F7F2F5' }
      },
        h('div', {
          style: { width: '36px', height: '36px', borderRadius: '10px', background: ph.bg, border: '1px solid ' + ph.border, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: '0' }
        }, makeIcon(ph.iconKey, ph.color, 18)),
        h('div', { style: { minWidth: '0' } },
          h('p', { style: { fontSize: '14px', fontWeight: '700', color: ph.color, margin: '0' } }, ph.label + ' · ' + ph.days),
          h('p', { style: { fontSize: '12px', color: '#777', margin: '2px 0 0', lineHeight: '1.45' } }, PHASE_TIPS[key])
        )
      ));
    });
    wrapper.appendChild(guideCard);

    // ── Update period date ──
    var calOpen = !!state.calcCalOpen;
    var updateCard = h('div', { style: { margin: '0 16px 16px' } });
    if (!calOpen) {
      updateCard.appendChild(h('button', {
        className: 'hf-btn',
        style: { width: '100%', padding: '13px 0', borderRadius: '14px', border: '1.5px solid #F4C0D1', background: '#fff', color: '#D4537E', fontSize: '14px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' },
        onClick: function () { setState({ calcCalOpen: true }); }
      }, makeIcon('calendar', '#D4537E', 16), 'Update period date'));
    } else {
      updateCard.appendChild(h('p', { style: { fontSize: '13px', fontWeight: '700', color: '#8B1A4A', margin: '0 0 8px' } }, 'When did your last period start?'));
      updateCard.appendChild(renderMiniCalendar(profile.lastPeriod || null, function (dateStr) {
        // Persist, then re-anchor predictions by refetching.
        Promise.resolve(fabscoreAPI.setPeriodDate(dateStr)).then(function (res) {
          if (res && res.ok) {
            evTrack('PeriodDate_Updated', {});
            if (state.profile) state.profile.lastPeriod = dateStr;
            showToast('Period date updated');
            _predFetched = false;
            setState({ predictions: null, calcCalOpen: false });
            loadPredictions();
          } else {
            showToast("Couldn't update your period date. Please try again.");
          }
        });
      }));
      updateCard.appendChild(h('button', {
        style: { width: '100%', background: 'none', border: 'none', color: '#999', fontSize: '13px', fontWeight: '600', cursor: 'pointer', padding: '10px 6px 0' },
        onClick: function () { setState({ calcCalOpen: false }); }
      }, 'Cancel'));
    }
    wrapper.appendChild(updateCard);

    wrapper.appendChild(renderBottomNav('calculator'));
    return wrapper;
  }

  // ─── SCREEN: SCORE ──────────────────────────────────────────
  // Fetch the Nector reward-points balance once (best-effort). Updates state so
  // the score screen can show it; never blocks rendering.
  var _walletFetched = false;
  function loadWalletBalance() {
    if (_walletFetched || DEV_SKIP_API) return;
    _walletFetched = true;
    Promise.resolve(fabscoreAPI.getWallet()).then(function (res) {
      if (res && res.ok && res.data && typeof res.data.balance === 'number') {
        setState({ walletBalance: res.data.balance });
      }
    }).catch(function () { });
  }

  function renderScore() {
    var log = state.todayLog || null;
    var profile = state.profile || {};
    var histLen = state.history ? state.history.length : 0;
    var m0Score = profile.initialScore || null;

    // Maturity
    var maturity = histLen < 7 ? 25 : histLen < 28 ? 50 : histLen < 56 ? 75 : 100;
    var maturityLabel = histLen < 7 ? 'Early' : histLen < 28 ? 'Month 1' : histLen < 56 ? 'Month 2' : 'Month 3+';

    var wrapper = h('div', { className: 'hf-score', style: { background: 'radial-gradient(130% 70% at 50% 0%, #FFF4F8 0%, #FFFFFF 50%)', minHeight: '100vh', paddingBottom: 'calc(84px + env(safe-area-inset-bottom, 0px))' } });
    wrapper.appendChild(renderLogoutBar());

    // Earn toast
    var toast = renderEarnToast();
    if (toast) wrapper.appendChild(toast);

    // Reward-points balance (Nector). Best-effort, one-time fetch.
    loadWalletBalance();
    if (state.walletBalance != null) {
      wrapper.appendChild(h('div', { style: { display: 'flex', justifyContent: 'center', padding: '12px 16px 0' } },
        h('div', {
          style: {
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            background: '#FFF0F6', border: '1px solid #F4C0D1', borderRadius: '999px',
            padding: '6px 14px', fontSize: '13px', fontWeight: '700', color: '#8B1A4A',
          }
        }, h('span', null, '★'), h('span', null, state.walletBalance + ' reward points'))
      ));
    }

    // ── Empty state: no log and no M0 score ──
    if (!log && !m0Score) {
      wrapper.appendChild(h('div', { style: { padding: '20px 16px 14px' } },
        h('p', { style: { fontSize: '16px', fontWeight: '700', color: '#111', margin: '0' } }, 'Health Score')
      ));
      var emptyCard = h('div', {
        style: { background: '#FFF0F6', borderRadius: '20px', padding: '32px 24px', textAlign: 'center', margin: '0 16px 16px' }
      });
      var flowerIcon = h('div', { style: { marginBottom: '12px', display: 'flex', justifyContent: 'center' } });
      flowerIcon.appendChild(makeIcon('heart', '#D4537E', 44));
      emptyCard.appendChild(flowerIcon);
      emptyCard.appendChild(h('p', { style: { fontSize: '18px', fontWeight: '700', color: '#D4537E', margin: '0 0 8px' } }, 'Setting up your score'));
      emptyCard.appendChild(h('p', { style: { fontSize: '14px', color: '#4A5568', lineHeight: '1.6', margin: '0 0 16px' } }, 'Your first check-in is on period day 1. healthfab will remind you automatically.'));
      emptyCard.appendChild(h('div', { style: { background: 'rgba(255,255,255,0.7)', borderRadius: '12px', padding: '12px 16px', textAlign: 'left' } },
        h('p', { style: { fontSize: '12px', fontWeight: '600', color: '#D4537E', margin: '0 0 6px' } }, 'Coming up:'),
        ...['Period approaching — daily check', 'Day 1 — flow + pain', 'Day 2 — energy + mood'].map(function (t) {
          return h('p', { style: { fontSize: '12px', color: '#4A5568', margin: '0 0 3px' } }, t);
        })
      ));
      wrapper.appendChild(emptyCard);
      wrapper.appendChild(renderBottomNav('score'));
      return wrapper;
    }

    // ── M0 baseline state: onboarding done but no check-in yet ──
    if (!log && m0Score) {
      var g0 = getGrade(m0Score);
      wrapper.appendChild(h('div', { style: { padding: '20px 20px 0' } },
        h('p', { style: { fontSize: '12px', color: '#bbb', margin: '0 0 2px' } }, 'Your starting point'),
        h('p', { style: { fontSize: '18px', fontWeight: '700', color: '#111', margin: '0' } }, (profile.name ? profile.name + '\'s' : 'Your') + ' Health Score')
      ));
      wrapper.appendChild(h('div', {
        style: { margin: '16px 16px 0', background: g0.bg, borderRadius: '24px', padding: '32px 24px', textAlign: 'center' }
      },
        renderGradeRing(m0Score, 120),
        h('p', { style: { fontSize: '13px', color: g0.color, opacity: '0.75', margin: '12px 0 0' } }, 'Based on your onboarding — score improves with each check-in')
      ));
      // Maturity bar
      wrapper.appendChild(h('div', {
        style: { margin: '12px 16px 0', background: '#f8f8f8', borderRadius: '14px', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px' }
      },
        h('div', { style: { flex: '1' } },
          h('p', { style: { fontSize: '11px', fontWeight: '600', color: '#999', margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '.05em' } }, 'Score accuracy'),
          h('div', { style: { height: '6px', background: '#e5e5e5', borderRadius: '3px' } },
            h('div', { style: { width: maturity + '%', height: '100%', background: '#D4537E', borderRadius: '3px' } })
          )
        ),
        h('span', { style: { fontSize: '12px', fontWeight: '700', color: '#D4537E', flexShrink: '0' } }, maturity + '% · ' + maturityLabel)
      ));
      var recsBaseline = renderProductRecs(m0Score);
      if (recsBaseline) wrapper.appendChild(recsBaseline);
      var cycleDay0 = getDayOfCycle(profile.lastPeriod, profile.cycleLength);
      wrapper.appendChild(renderNextCheckinCard(getNextTouchpoint(cycleDay0)));
      wrapper.appendChild(renderBottomNav('score'));
      return wrapper;
    }

    // ── Full score screen ──
    var score = log.score || 50;
    var gradeInfo = getGrade(score);
    var plan = ACTION_PLANS[gradeInfo.grade] || ACTION_PLANS.C;
    var ph = log.phase ? PHASES[log.phase] : PHASES.follicular;

    // Delta from previous log
    var prev = state.history && state.history.length >= 2 ? state.history[state.history.length - 2] : null;
    var delta = prev ? score - (prev.score || 0) : null;

    // 4 sub-scores (Nura-style)
    var coreAns = log.answers || {};
    var subScores = [
      { key: 'Cycle', score: Math.min(100, 40 + histLen * 5) },
      { key: 'Symptoms', score: log ? Math.round(((coreAns.M1Q1 && coreAns.M1Q1.s) || (coreAns.M2Q1 && coreAns.M2Q1.s) || 70)) : 70 },
      { key: 'Nutrition', score: (coreAns.M3Q1 && coreAns.M3Q1.s) || 70 },
      { key: 'Sleep', score: (coreAns.M5Q1 && coreAns.M5Q1.s) || (coreAns.M4Q1 && coreAns.M4Q1.s) || 70 },
    ];

    // Header
    wrapper.appendChild(h('div', { style: { padding: '20px 20px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' } },
      h('div', null,
        h('p', { style: { fontSize: '12px', color: '#bbb', margin: '0 0 2px' } }, 'Cycle day ' + (log.cycleDay || '') + ' · ' + (ph ? ph.label : '')),
        h('p', { style: { fontSize: '18px', fontWeight: '700', color: '#111', margin: '0' } }, (profile.name ? profile.name + '\'s' : 'Your') + ' Health Score')
      )
    ));

    // Main grade card
    wrapper.appendChild(h('div', {
      style: { margin: '0 16px 12px', background: gradeInfo.bg, borderRadius: '24px', padding: '28px 24px', textAlign: 'center' }
    },
      renderGradeRing(score, 130),
      h('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '12px' } },
        h('span', { style: { fontSize: '13px', color: gradeInfo.color, opacity: '0.7' } }, score + '/100'),
        delta !== null ? h('span', {
          style: {
            fontSize: '12px', fontWeight: '700',
            color: delta >= 0 ? '#2BA84A' : '#E53E3E',
            background: delta >= 0 ? '#F0FBF2' : '#FFF5F5',
            padding: '2px 8px', borderRadius: '8px'
          }
        }, (delta >= 0 ? '↑ ' + delta : '↓ ' + Math.abs(delta)) + ' from last') : null
      ),
      h('p', { style: { fontSize: '12px', color: gradeInfo.color, opacity: '0.65', margin: '6px 0 0' } }, gradeInfo.desc)
    ));

    // 2×2 sub-score grid
    wrapper.appendChild(h('div', {
      style: { margin: '0 16px 12px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }
    },
      ...subScores.map(function (ss) {
        var sg = getGrade(ss.score);
        return h('div', {
          style: { background: sg.bg, borderRadius: '18px', padding: '16px 12px', textAlign: 'center', border: '1px solid ' + sg.ringColor + '30' }
        },
          renderGradeRing(ss.score, 72),
          h('p', { style: { fontSize: '11px', fontWeight: '600', color: sg.color, margin: '8px 0 0' } }, ss.key)
        );
      })
    ));

    // Maturity strip
    wrapper.appendChild(h('div', {
      style: { margin: '0 16px 12px', background: '#f8f8f8', borderRadius: '14px', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px' }
    },
      h('div', { style: { flex: '1' } },
        h('p', { style: { fontSize: '11px', fontWeight: '600', color: '#999', margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '.05em' } }, 'Score accuracy'),
        h('div', { style: { height: '6px', background: '#e5e5e5', borderRadius: '3px' } },
          h('div', { style: { width: maturity + '%', height: '100%', background: '#D4537E', borderRadius: '3px', transition: 'width .6s' } })
        )
      ),
      h('span', { style: { fontSize: '12px', fontWeight: '700', color: '#D4537E', flexShrink: '0' } }, maturity + '% · ' + maturityLabel)
    ));

    // Phase insight strip
    if (ph) {
      var phaseInsightText = '';
      var phaseName = log.phase || '';
      if (phaseName === 'menstrual') phaseInsightText = 'Your body is doing its hardest work. Pain and fatigue are expected — not weakness.';
      else if (phaseName === 'follicular') phaseInsightText = 'Energy rising. Brain and body at their best this week. Use it.';
      else if (phaseName === 'ovulatory') phaseInsightText = 'Peak week. Confidence, strength, and clarity are at their monthly high.';
      else if (phaseName === 'luteal') phaseInsightText = 'Wind-down phase. Bloating and mood shifts are hormonal — not you.';

      if (phaseInsightText) {
        wrapper.appendChild(h('div', {
          style: { margin: '0 16px 12px', background: ph.bg, border: '1.5px solid ' + ph.border, borderRadius: '14px', padding: '14px 16px' }
        },
          h('p', { style: { fontSize: '13px', fontWeight: '700', color: ph.color, margin: '0 0 4px' } }, ph.label + ' phase — day ' + (log.cycleDay || '')),
          h('p', { style: { fontSize: '12px', color: ph.color, opacity: '0.8', margin: '0', lineHeight: '1.5' } }, phaseInsightText)
        ));
      }
    }

    // Action plan
    var planIconName = gradeInfo.grade === 'A' ? 'check' : gradeInfo.grade === 'B' ? 'chart' : gradeInfo.grade === 'C' ? 'alert' : 'alert';
    var planHeader = h('div', { style: { display: 'flex', alignItems: 'center', gap: '8px', margin: '0 0 14px' } });
    planHeader.appendChild(makeIcon(planIconName, gradeInfo.color, 18));
    planHeader.appendChild(h('p', { style: { fontSize: '14px', fontWeight: '800', color: gradeInfo.color, margin: '0' } }, plan.title));

    wrapper.appendChild(h('div', {
      style: { margin: '0 16px 12px', background: gradeInfo.bg, border: '1.5px solid ' + gradeInfo.ringColor + '30', borderRadius: '18px', padding: '16px' }
    },
      planHeader,
      h('div', { style: { display: 'flex', flexDirection: 'column', gap: '10px' } },
        ...plan.actions.map(function (a) {
          var actionRow = h('div', {
            style: { display: 'flex', gap: '10px', alignItems: 'flex-start', padding: '10px 12px', background: 'rgba(255,255,255,0.7)', borderRadius: '12px' }
          });
          var actionIcon = h('div', { style: { flexShrink: '0', marginTop: '2px' } });
          actionIcon.appendChild(makeIcon(a.iconKey || 'check', gradeInfo.color, 16));
          actionRow.appendChild(actionIcon);
          actionRow.appendChild(h('div', null,
            h('p', { style: { fontSize: '12px', fontWeight: '700', color: gradeInfo.color, margin: '0 0 2px' } }, a.label),
            h('p', { style: { fontSize: '12px', color: '#444', lineHeight: '1.5', margin: '0' } }, a.text)
          ));
          return actionRow;
        })
      ),
      gradeInfo.grade === 'D' ? h('button', {
        style: { width: '100%', marginTop: '12px', padding: '12px 0', borderRadius: '12px', border: 'none', background: '#E53E3E', color: '#fff', fontSize: '14px', fontWeight: '700', cursor: 'pointer' }
      }, 'Book gynaec consult — ₹49') : null
    ));

    var recsFull = renderProductRecs(log && log.score);
    if (recsFull) wrapper.appendChild(recsFull);
    wrapper.appendChild(renderBottomNav('score'));
    return wrapper;
  }

  // ─── ROUTER ─────────────────────────────────────────────────
  var LIQUID_SCREENS = ['welcome', 'product_first'];
  var APP_SCREENS = ['loading', 'login', 'onboard', 'checkin', 'calculator', 'score'];

  const SCREENS = {
    loading: renderLoading,
    login: renderLogin,
    onboard: renderOnboarding,
    checkin: renderCheckin,
    calculator: renderCalculator,
    score: renderScore,
  };

  /** Show exactly one screen — collapse inactive Shopify section wrappers to 0 height (like old SPA). */
  function syncScreenVisibility(screen) {
    var prev = document.body.dataset.hfScreen || '';
    document.body.dataset.hfScreen = screen || '';

    document.querySelectorAll('[data-hf-screen]').forEach(function (el) {
      var match = el.getAttribute('data-hf-screen') === screen;
      el.classList.toggle('hf-screen-active', match);
      el.hidden = !match;
      el.style.display = match ? 'block' : 'none';

      var section = el.closest('.shopify-section');
      if (section) {
        if (match) {
          section.style.removeProperty('display');
          section.setAttribute('data-hf-section-active', 'true');
        } else {
          section.style.setProperty('display', 'none', 'important');
          section.setAttribute('data-hf-section-active', 'false');
        }
      }
    });

    var container = app();
    var showApp = APP_SCREENS.indexOf(screen) !== -1;
    if (container) {
      container.classList.toggle('hf-screen-active', showApp);
      container.hidden = !showApp;
      if (showApp) {
        container.style.removeProperty('display');
        container.style.display = 'block';
      } else {
        container.style.setProperty('display', 'none', 'important');
      }

      var shellSection = container.closest('.shopify-section');
      if (shellSection) {
        if (showApp) {
          shellSection.style.removeProperty('display');
          shellSection.setAttribute('data-hf-section-active', 'true');
        } else {
          shellSection.style.setProperty('display', 'none', 'important');
          shellSection.setAttribute('data-hf-section-active', 'false');
        }
      }
    }

    if (prev !== screen) {
      try { window.scrollTo(0, 0); } catch (e) { }
    }
  }

  function render() {
    var screen = state.screen || 'loading';
    syncScreenVisibility(screen);

    var container = app();
    var isLiquid = LIQUID_SCREENS.indexOf(screen) !== -1;

    if (isLiquid) {
      if (container) container.innerHTML = '';
      if (screen === 'product_first') hydrateProductFirst();
    } else {
      if (!container) return;
      var renderFn = SCREENS[screen] || renderLoading;
      try {
        var content = renderFn();
        container.innerHTML = '';
        if (content) container.appendChild(content);
      } catch (err) {
        console.error('[FabScore] render failed:', screen, err);
        container.innerHTML = '';
        container.appendChild(h('div', { className: 'hf-loading', style: { padding: '40px 20px', textAlign: 'center' } },
          h('p', { className: 'hf-loading__text' }, 'Something went wrong. Please refresh.'),
          h('button', {
            type: 'button',
            className: 'hf-btn hf-btn--cta',
            style: { marginTop: '16px' },
            onClick: function () { location.reload(); }
          }, 'Refresh')
        ));
      }

      if (screen === 'login') {
        var ae = document.activeElement;
        var inApp = ae && ae.id && ae.id.indexOf('hf-') === 0;
        if (!inApp) focusEl((state.loginStep || 'phone') === 'otp' ? 'hf-otp-0' : 'hf-phone-input');
      }
    }

    syncBottomNavViewport();
    fabscoreTrack('screen_view', { screen: screen });
    if (state.screen !== _lastTrackedScreen) {
      // Reset the calculator one-time track guard when leaving the screen so it
      // re-fires on the next open.
      if (_lastTrackedScreen === 'calculator' && state.screen !== 'calculator') _calcTracked = false;
      _lastTrackedScreen = state.screen;
      // renderCalculator emits its own guarded Screen_Viewed; avoid double-firing.
      if (state.screen !== 'calculator') evTrack('Screen_Viewed', { screen: state.screen });
      if (state.screen === 'score') evTrack('Score_Viewed', {});
      if (state.screen === 'onboard' && !_onbStarted) { _onbStarted = true; evTrack('Onboarding_Started', {}); }
    }
  }
  var _lastTrackedScreen = null;
  var _onbStarted = false;
  var _ckStarted = {};

  // After a session is established, route by profile/today's-log.
  async function routeAfterAuth() {
    try {
      var profileRes = await fabscoreAPI.getProfile();
      if (profileRes.status === 401 || profileRes.code === 'AUTH_REQUIRED' || profileRes.code === 'AUTH_EXPIRED') {
        setState({ screen: 'login', loginStep: 'phone' });
        return;
      }
      if (profileRes.ok && profileRes.data) {
        state.profile = profileRes.data;
        var logRes = await fabscoreAPI.getLog(TODAY);
        if (logRes.ok && logRes.data) setState({ todayLog: logRes.data, screen: 'score' });
        else setState({ screen: 'checkin' });
      } else {
        // 404 PROFILE_NOT_FOUND → onboarding entry.
        setState({ screen: 'welcome' });
      }
    } catch (err) {
      fabscoreTrack('route_error', { error: err.message });
      setState({ screen: 'welcome' });
    }
  }

  // ─── BOTTOM NAV: pin to the visual viewport ──────────────────
  // WhatsApp's in-app browser shows/hides its own top+bottom chrome as the
  // user scrolls. A plain `position: fixed` nav is pinned to the layout
  // viewport, not the momentarily-shrunk visual one, so it visibly fights
  // that chrome animation (squeezes/jumps) until the transition settles.
  // Re-anchor it to the real visible bottom edge on every visualViewport
  // change so it never drifts out of sync with the host browser's chrome.
  function syncBottomNavViewport() {
    if (!window.visualViewport) return;
    var nav = /** @type {HTMLElement} */ (document.querySelector('.hf-bottom-nav'));
    if (!nav) return;
    var vv = window.visualViewport;
    var hiddenBy = window.innerHeight - (vv.height + vv.offsetTop);
    nav.style.transform = 'translateX(-50%) translateY(' + (hiddenBy > 0 ? -hiddenBy : 0) + 'px)';
  }

  // Discrete visualViewport 'resize'/'scroll' events land at inconsistent
  // timing across Android OEM WebView builds (Samsung/MIUI/etc.) — on some
  // screen sizes they fire promptly, on others late or not at all, so the
  // nav visibly lags/drags mid-scroll. Drive the correction from a
  // requestAnimationFrame loop instead, kept alive for as long as any
  // scroll is happening (plus a short settle window), so every device
  // gets a same-frame correction regardless of whether it fires those
  // events cleanly.
  var _navSyncActive = false;
  var _navSyncIdleTimer = null;
  function navSyncFrame() {
    syncBottomNavViewport();
    if (_navSyncActive) requestAnimationFrame(navSyncFrame);
  }
  function pokeNavSync() {
    if (!_navSyncActive) {
      _navSyncActive = true;
      requestAnimationFrame(navSyncFrame);
    }
    clearTimeout(_navSyncIdleTimer);
    _navSyncIdleTimer = setTimeout(function () { _navSyncActive = false; }, 250);
  }

  function bindBottomNavViewportSync() {
    window.addEventListener('scroll', pokeNavSync, { passive: true, capture: true });
    window.addEventListener('touchmove', pokeNavSync, { passive: true });
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', pokeNavSync);
      window.visualViewport.addEventListener('scroll', pokeNavSync);
    }
  }

  // ─── THEME EDITOR: show the section you're editing ──────────
  function screenFromSectionId(sectionId) {
    if (!sectionId) return null;
    var id = String(sectionId).toLowerCase();
    if (id.indexOf('product_first') !== -1 || id.indexOf('product-first') !== -1) return 'product_first';
    if (id.indexOf('welcome') !== -1) return 'welcome';
    // Shell mounts login/onboard/score — NEVER force login (that bounced CTA → onboard → login).
    // When editor selects the shell, preview onboarding step 1.
    if (id.indexOf('fabscore') !== -1) return 'onboard';
    return null;
  }

  function bindThemeEditorScreens() {
    if (!(window.Shopify && window.Shopify.designMode)) return;

    document.addEventListener('shopify:section:select', function (event) {
      if (Date.now() < _ignoreSectionSelectUntil) return;
      var screen = screenFromSectionId(event.detail && event.detail.sectionId);
      if (!screen) return;
      if (screen === state.screen) return;
      setState({ screen: screen });
      if (screen === 'product_first') hydrateProductFirst();
    });

    document.addEventListener('shopify:section:load', function (event) {
      // The merchant may have just changed the pixel settings; they ride on
      // window.__fabscore, which the re-rendered section rewrites.
      _pixelCfg = null;
      if (Date.now() < _ignoreSectionSelectUntil) return;
      var screen = screenFromSectionId(event.detail && event.detail.sectionId);
      if (screen === 'product_first' || state.screen === 'product_first') hydrateProductFirst();
      _welcomeBound = false;
      bindWelcomeInteractions();
    });
  }

  // ─── INIT ───────────────────────────────────────────────────
  async function init() {
    document.documentElement.classList.add('hf-booted');
    bindLiquidNavigation();
    bindWelcomeInteractions();
    bindThemeEditorScreens();
    bindBottomNavViewportSync();

    // Theme Editor: show welcome so sections aren't blank white
    if (window.Shopify && window.Shopify.designMode) {
      setState({ screen: 'welcome' });
      fabscoreTrack('app_init', { params: state.urlParams, designMode: true });
      return;
    }

    // Show loading in the app mount while we resolve session
    setState({ screen: 'loading' });
    fabscoreTrack('app_init', { params: state.urlParams });
    // Force-flush: the landing token only ever leaves the browser as part of
    // an events POST, and this is the very first event of the session. Left
    // on the default 5s-interval/pagehide-beacon batching, a WhatsApp in-app
    // WebView that closes in the first couple of seconds never sends it —
    // "Link Open" fires (BIK's own webhook) but "Landed" never does.
    evTrack('Session_Started', { entryProduct: state.urlParams.product, entryRef: state.urlParams.ref }, true);

    // Dev mode: skip API entirely, go straight to welcome.
    if (DEV_SKIP_API) { setState({ screen: 'welcome' }); return; }

    // 1. Already have a valid session (first-party cookie or stored bearer)?
    var sess = await fabscoreAPI.getSession();
    if (sess.ok && sess.data) {
      state.customer = { loggedIn: true, phone: sess.data.phone };
      await routeAfterAuth();
      return;
    }

    // 2. Not logged in → load which auth methods are enabled, then show login.
    var cfg = await fabscoreAPI.getAuthConfig();
    state.authConfig = (cfg.ok && cfg.data) ? cfg.data : { methods: { otp: true, shopify: true }, default: 'otp' };
    setState({ screen: 'login', loginStep: 'phone' });
  }

  // ─── DEV: test hooks — local theme-dev only, never in production ──────
  if (IS_LOCAL) {
    window.__fabscoreDebug = {
      showScreen: function (name) { setState({ screen: name }); },
      getState: function () { return state; },
    };
  }

  // Boot
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();